import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { supabase } from "../../lib/supabase";
import { useTabParam } from "../../lib/useTabParam";
import { useAuth } from "../../context/AuthContext";
import { useRealtimeTable } from "../../lib/useRealtimeTable";
import type { Deal, DealPayment, TeamMember, TeamRole } from "../../lib/types";
import { fmtInt, fmtUSD } from "../../lib/analytics";
import SupplementEntryWarning from "../shared/SupplementEntryWarning";
import { formatSupabaseError } from "../../lib/formatSupabaseError";
import { heicToJpeg } from "../../lib/heicToJpeg";

import DealLink from "../DealLink";
import DealAgeChips from "../shared/DealAgeChips";
import { daysInStage } from "../../lib/dealAge";
import CommissionBoard from "../commissions/CommissionBoard";
import CheckTrackerCard from "../payments/CheckTrackerCard";
import PaymentEditModal from "../payments/PaymentEditModal";
import DrawsTab from "./DrawsTab";
import CanvassersFinanceTab from "./CanvassersFinanceTab";
import ReferralsFinanceTab from "./ReferralsFinanceTab";
import SpiffsFinanceTab from "./SpiffsFinanceTab";
import InvoiceReportTab from "./InvoiceReportTab";
import DisputedPaymentCard from "./DisputedPaymentCard";
import CommissionValidationPanel from "./CommissionValidationPanel";
import ReconcileButton from "./ReconcileButton";
import { SendInvoicePanel } from "./SendInvoicePanel";
import { AutoRemindChip } from "./AutoRemindToggle";
import { REMIND_STAGES, type ReminderRow, type ReminderSettings } from "../../lib/autoRemind";
import {
  SUPPLEMENT_SENT_TAG,
  SUPPLEMENT_SENT_TEXT,
  SUPPLEMENT_TEXT_STAGES,
  autoTextEta,
  supplementAutoText,
  suppsDateTextsAutomatically,
  type AutoTextEnv,
  type AutoTextRow,
  type AutoTextState,
} from "../../lib/homeownerTexts";
import { etDate } from "../../lib/weeklySpiffs";
import { toE164 } from "../../lib/phoneFormat";
import CommsStrip from "../comms/CommsStrip";
import DealCommsDrawer from "../comms/DealCommsDrawer";
import { fetchCommsMap } from "../../lib/fetchCommsMap";
import { withOptimisticOutbound, type CommsMap, type DealCommsSummary } from "../../lib/commsStrip";
import { logButtonClick } from "../../lib/uiEvents";

/**
 * Finance Board — Ship 4 (May 2, 2026 — Finance Board Redesign).
 *
 * Role-pill shell mirroring DispatchBoard's pattern:
 *   💰 Shannon (Finance) — Supplements / Payments / Commissions / AR Aging
 *   👔 Chad + Jennifer    — Commissions / Supplements / Draws
 *   📊 Executive          — AR / Commissions / Supplements / Payments / Draws
 *
 * Default view derived from useAuth().role. URL-backed via useTabParam so back-
 * navigation from a deal detail returns to the same view + tab. Closing
 * Pipeline tab dropped per Greg's review (redundant with Supplements).
 *
 * The component file name remains FinanceDashboard.tsx so existing routes /
 * imports keep working without a multi-file move; the exported default is the
 * new role-pill shell. SupplementsTab / ARAgingTab / DealsLinkedPaymentsTab
 * are kept inline as the v1 tab bodies.
 */

// ── Role view definitions ──
type ViewAsKey = "shannon" | "chad" | "executive";

const VIEW_AS_OPTIONS: Array<{ key: ViewAsKey; label: string; emoji: string }> = [
  { key: "shannon",   emoji: "💰", label: "Shannon (Finance)" },
  { key: "chad",      emoji: "👔", label: "Chad + Jennifer (Approvals)" },
  { key: "executive", emoji: "📊", label: "Executive" },
];

type FinanceTabKey = "supplements" | "payments" | "commissions" | "ar" | "draws" | "canvassers" | "spiffs" | "invoice_report" | "referrals";

const TABS_BY_VIEW: Record<ViewAsKey, Array<{ key: FinanceTabKey; label: string }>> = {
  shannon: [
    { key: "supplements", label: "Supplements" },
    { key: "invoice_report", label: "Invoice Report" },
    { key: "payments",    label: "Payments" },
    { key: "commissions", label: "Commissions" },
    { key: "spiffs",      label: "SPIFFs" },
    { key: "referrals",   label: "Referrals" },
    { key: "ar",          label: "AR Aging" },
    { key: "canvassers",  label: "Canvassers" },
  ],
  chad: [
    { key: "commissions", label: "Commissions" },
    { key: "supplements", label: "Supplements" },
    { key: "invoice_report", label: "Invoice Report" },
    { key: "draws",       label: "Draws" },
    { key: "referrals",   label: "Referrals" },
  ],
  executive: [
    { key: "ar",          label: "AR Aging" },
    { key: "commissions", label: "Commissions" },
    { key: "supplements", label: "Supplements" },
    { key: "invoice_report", label: "Invoice Report" },
    { key: "payments",    label: "Payments" },
    { key: "spiffs",      label: "SPIFFs" },
    { key: "draws",       label: "Draws" },
    { key: "canvassers",  label: "Canvassers" },
    { key: "referrals",   label: "Referrals" },
  ],
};

const SUBTITLES: Record<ViewAsKey, string> = {
  shannon:   "Supplement progress · payments collected/cleared · QuickBooks reconciliation",
  chad:      "Commission approvals · supplement notes · draw approvals · roof-crew approvals",
  executive: "KPIs, AR exposure, approval pipeline, cash position",
};

function defaultViewForRole(role: TeamRole | undefined, isSuperUser: boolean): ViewAsKey {
  if (role === "finance") return "shannon";
  if (role === "sales_director") return "chad";
  if (role === "qc" || role === "director_of_operations" || role === "roof_director") return "chad";
  if (role === "admin" || isSuperUser || role === "asm") return "executive";
  return "executive";
}

export default function FinanceDashboard() {
  const { role: userRole, isSuperUser } = useAuth();
  const myDefault = defaultViewForRole(userRole, isSuperUser);

  const [view, setView] = useTabParam<ViewAsKey>("view", myDefault, [
    "shannon", "chad", "executive",
  ]);

  const tabs = useMemo(() => TABS_BY_VIEW[view], [view]);
  const validTabKeys = useMemo(() => tabs.map((t) => t.key), [tabs]);
  const defaultTabKey = tabs[0]?.key ?? "supplements";
  const [tab, setTab] = useTabParam<FinanceTabKey>("tab", defaultTabKey, validTabKeys);

  function handleViewChange(newView: ViewAsKey) {
    setView(newView);
    const newTabs = TABS_BY_VIEW[newView];
    setTab(newTabs[0]?.key ?? "supplements");
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="mb-3">
        <h1 className="text-2xl font-semibold text-bardin-ink">Finance Board</h1>
        <p className="text-sm text-slate-500 mt-1">{SUBTITLES[view]}</p>
      </div>

      {/* Role pill bar — matches DispatchBoard's pattern */}
      <div className="bg-white border border-slate-200 rounded-xl px-3 py-2.5 mb-5 overflow-x-auto whitespace-nowrap flex gap-1.5">
        {VIEW_AS_OPTIONS.map((opt) => (
          <button
            key={opt.key}
            onClick={() => handleViewChange(opt.key)}
            className={
              "inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[13px] font-medium border transition flex-shrink-0 " +
              (view === opt.key
                ? "bg-bardin-orange text-white border-bardin-orange shadow-sm"
                : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50")
            }
          >
            <span>{opt.emoji}</span>
            {opt.label}
            {opt.key === myDefault ? " (You)" : ""}
          </button>
        ))}
      </div>

      {/* KPI strip — view-specific */}
      <FinanceKPIs view={view} onTab={(k) => setTab(k)} />

      {/* Tab strip */}
      <div className="border-b-2 border-slate-200 mb-5">
        <nav className="flex gap-1 flex-wrap" aria-label="Finance tabs">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={
                "inline-flex items-center gap-1.5 px-4 py-2.5 text-[13px] font-semibold -mb-px border-b-2 whitespace-nowrap transition-all " +
                (tab === t.key
                  ? "text-bardin-orange border-bardin-orange"
                  : "text-slate-500 border-transparent hover:text-slate-700")
              }
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab body */}
      {tab === "supplements" && <SupplementsTab />}
      {tab === "payments"    && <ShannonPaymentsTab />}
      {tab === "commissions" && (
        <>
          {/* Commission Queue Validation — Executive role pill, super users only.
              On-demand backstop replacing the deprecated commission-pre-validator
              scheduled task. Renders above the embedded CommissionBoard so the
              "is this queue safe to approve?" answer is visible before the
              detailed list. May 2, 2026. */}
          {view === "executive" && isSuperUser && <CommissionValidationPanel />}
          <CommissionBoard embedded />
        </>
      )}
      {tab === "ar"          && <ARAgingTab />}
      {tab === "invoice_report" && <InvoiceReportTab />}
      {tab === "draws"       && <DrawsTab />}
      {tab === "canvassers"  && <CanvassersFinanceTab />}
      {tab === "referrals"   && <ReferralsFinanceTab />}
      {tab === "spiffs"      && <SpiffsFinanceTab />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AR Aging — escalation buckets per payment-collection spec
// 14 Yellow, 21 (Chad notified), 28 Orange, 30 (formal notice, 1.5%/mo),
// 45 Red (Greg), 60 Black (collections, warranty void)
// ---------------------------------------------------------------------------

type BucketKey = "current" | "yellow" | "orange" | "red" | "black";
const BUCKETS: Array<{ key: BucketKey; label: string; class: string }> = [
  { key: "current", label: "0-13 days", class: "bg-slate-100 text-slate-700" },
  { key: "yellow", label: "14-27 days", class: "bg-yellow-100 text-yellow-800" },
  { key: "orange", label: "28-44 days", class: "bg-orange-100 text-orange-800" },
  { key: "red", label: "45-59 days", class: "bg-red-100 text-red-700" },
  { key: "black", label: "60+ days", class: "bg-black text-white" },
];

function bucketize(days: number): BucketKey {
  if (days >= 60) return "black";
  if (days >= 45) return "red";
  if (days >= 28) return "orange";
  if (days >= 14) return "yellow";
  return "current";
}

// Cleared money actually paid against a deal's invoice. The legacy
// `final_payment_paid` column is null on nearly every deal — real payments live
// in the trigger-maintained `pre_build_amount_received` / `post_build_amount_received`
// columns (cleared/bank_cleared only) plus any `balance_written_off`. Use the
// GREATER of (received columns) vs (legacy final_payment_paid) so we never lose a
// legacy manual entry, and never double-count a payment tracked both ways. Mirrors
// the system's `post_build_balance_remaining` definition (cleared-only — AR excludes
// in-flight by design).
function arPaidAgainst(r: {
  final_payment_paid?: number | string | null;
  pre_build_amount_received?: number | string | null;
  post_build_amount_received?: number | string | null;
  balance_written_off?: number | string | null;
}): number {
  const received =
    (Number(r.pre_build_amount_received) || 0) +
    (Number(r.post_build_amount_received) || 0);
  const legacy = Number(r.final_payment_paid) || 0;
  return Math.max(received, legacy) + (Number(r.balance_written_off) || 0);
}

function arBalanceOwed(r: {
  final_invoice_amount?: number | string | null;
  final_payment_paid?: number | string | null;
  pre_build_amount_received?: number | string | null;
  post_build_amount_received?: number | string | null;
  balance_written_off?: number | string | null;
}): number {
  return (Number(r.final_invoice_amount) || 0) - arPaidAgainst(r);
}

function ARAgingTab() {
  type Row = Pick<
    Deal,
    | "id"
    | "homeowner_name"
    | "homeowner_phone"
    | "property_street"
    | "claim_number"
    | "stage"
    | "final_invoice_sent_date"
    | "build_completed_date"
    | "final_invoice_amount"
    | "final_payment_paid"
    | "pre_build_amount_received"
    | "post_build_amount_received"
    | "balance_written_off"
    | "owner_id"
  >;

  const { member } = useAuth();
  const [rows, setRows] = useState<Row[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Log Contact modal state
  const [logTarget, setLogTarget] = useState<{ id: string; name: string } | null>(null);
  const [contactNote, setContactNote] = useState("");
  const [logBusy, setLogBusy] = useState(false);
  // Per-deal busy flags (keyed by deal id + action)
  const [textBusy, setTextBusy] = useState<string | null>(null);
  const [demandBusy, setDemandBusy] = useState<string | null>(null);
  // Active bucket filter — set by clicking a summary card, cleared by clicking again
  const [activeBucket, setActiveBucket] = useState<BucketKey | null>(null);
  // Confirmation modal state — holds the pending send so the user can review before firing
  type AgedItem = { row: Row; days: number; owed: number; bucket: BucketKey };
  const [confirmPending, setConfirmPending] = useState<{
    type: "demand" | "text";
    x: AgedItem;
    phone: string;
    body: string;
  } | null>(null);
  // Toast
  const [toast, setToast] = useState<string | null>(null);
  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3500);
  }

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const { data, error } = await supabase
        .from("deals")
        .select(
          "id,homeowner_name,homeowner_phone,property_street,claim_number,stage,final_invoice_sent_date,build_completed_date,final_invoice_amount,final_payment_paid,pre_build_amount_received,post_build_amount_received,balance_written_off,owner_id",
        )
        .in("stage", ["roof_complete", "trades_in_progress", "collecting", "commission_ready",
          "ready_for_supps", "ready_for_updated_invoice", "supps_waiting_on_final_invoice",
          "delinquent", "register_oc_warranty"]);
      if (error) {
        if (!cancelled) setErr(error.message);
        return;
      }
      if (!cancelled) setRows((data ?? []) as Row[]);
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const aged = useMemo(() => {
    if (!rows) return [] as Array<{ row: Row; days: number; owed: number; bucket: BucketKey }>;
    const now = Date.now();
    return rows
      .map((r) => {
        const anchor = r.final_invoice_sent_date ?? r.build_completed_date;
        const owed = Math.max(0, arBalanceOwed(r));
        const days = anchor
          ? Math.floor((now - new Date(anchor).getTime()) / 86_400_000)
          : 0;
        return { row: r, days, owed, bucket: bucketize(days) };
      })
      .filter((x) => x.owed > 0)
      .sort((a, b) => b.days - a.days);
  }, [rows]);

  const bucketTotals = useMemo(() => {
    const t: Record<BucketKey, { count: number; value: number }> = {
      current: { count: 0, value: 0 },
      yellow: { count: 0, value: 0 },
      orange: { count: 0, value: 0 },
      red: { count: 0, value: 0 },
      black: { count: 0, value: 0 },
    };
    for (const x of aged) {
      t[x.bucket].count += 1;
      t[x.bucket].value += x.owed;
    }
    return t;
  }, [aged]);

  // Deals needing action (yellow and worse)
  const actionItems = useMemo(
    () => aged.filter((x) => x.bucket !== "current"),
    [aged],
  );
  const escalations = useMemo(
    () => aged.filter((x) => x.bucket === "red" || x.bucket === "black"),
    [aged],
  );

  // Deals in active collections (collecting / delinquent) that have NO final
  // invoice amount entered yet. Their `owed` computes to $0, so the `owed > 0`
  // filter on `aged` drops them entirely and they vanish from AR Aging — finance
  // never sees that the invoice still needs to be entered to start collecting.
  // Surface them as a separate exceptions list (kept out of the aging math so
  // bucket totals + the printed AR report stay about real balances).
  const needsInvoice = useMemo(() => {
    if (!rows) return [] as Row[];
    const COLLECTIONS_STAGES = new Set(["collecting", "delinquent"]);
    return rows
      .filter(
        (r) =>
          COLLECTIONS_STAGES.has(r.stage ?? "") &&
          !(Number(r.final_invoice_amount) > 0),
      )
      .sort((a, b) => (a.homeowner_name ?? "").localeCompare(b.homeowner_name ?? ""));
  }, [rows]);

  function toE164(phone: string | null | undefined): string | null {
    if (!phone) return null;
    const d = phone.replace(/\D/g, "");
    if (d.length === 10) return `+1${d}`;
    if (d.length === 11 && d[0] === "1") return `+${d}`;
    return null;
  }

  // Body builders — called when the confirm modal opens so the user sees the
  // exact text before it goes out. The send functions reuse the same body.
  function buildPaymentLinkBody(x: AgedItem): string {
    const firstName = x.row.homeowner_name?.split(" ")[0] ?? "there";
    const street = x.row.property_street ?? "your property";
    return (
      `Hi ${firstName}, this is Shannon at Bardin Roofing. You have a remaining balance of ` +
      `${fmtUSD(x.owed)} for your roof at ${street}. Pay online: portal.bardinroofing.com ` +
      `— or call us at (470) 823-8816.`
    );
  }

  function buildFinalDemandBody(x: AgedItem): string {
    const firstName = x.row.homeowner_name?.split(" ")[0] ?? "there";
    const street = x.row.property_street ?? "your property";
    return (
      `[FINAL NOTICE] Hi ${firstName}, your Bardin Roofing balance of ${fmtUSD(x.owed)} ` +
      `at ${street} is now ${x.days} days past due. Immediate payment is required to ` +
      `preserve your warranty. Pay: portal.bardinroofing.com or call (470) 823-8816.`
    );
  }

  // Open the confirm modal — phone guard runs here so the modal only opens when
  // a number is actually on file.
  function openConfirm(type: "demand" | "text", x: AgedItem) {
    const phone = toE164(x.row.homeowner_phone);
    if (!phone) { showToast("No phone number on file for this homeowner"); return; }
    const body = type === "demand" ? buildFinalDemandBody(x) : buildPaymentLinkBody(x);
    setConfirmPending({ type, x, phone, body });
  }

  async function sendPaymentLink(x: AgedItem, body: string) {
    const phone = toE164(x.row.homeowner_phone);
    if (!phone) { showToast("No phone number on file for this homeowner"); return; }
    setTextBusy(x.row.id);
    try {
      const { error: smsErr } = await supabase.functions.invoke("twilio-send-sms", {
        body: { to: phone, body, deal_id: x.row.id, tag: "ar_payment_link" },
      });
      if (smsErr) throw smsErr;
      await supabase.from("activity_log").insert({
        deal_id: x.row.id,
        activity_type: "ar_contact",
        title: "Payment link texted to homeowner",
        description: `Payment link SMS sent to ${x.row.homeowner_name ?? "homeowner"} — balance ${fmtUSD(x.owed)}.`,
        metadata: { by: member?.full_name ?? null, tag: "ar_payment_link" },
      });
      showToast("✓ Payment link sent");
    } catch {
      showToast("Failed to send text — check phone number");
    } finally {
      setTextBusy(null);
    }
  }

  async function sendFinalDemand(x: AgedItem, body: string) {
    const phone = toE164(x.row.homeowner_phone);
    if (!phone) { showToast("No phone number on file for this homeowner"); return; }
    setDemandBusy(x.row.id);
    try {
      const { error: smsErr } = await supabase.functions.invoke("twilio-send-sms", {
        body: { to: phone, body, deal_id: x.row.id, tag: "ar_final_demand" },
      });
      if (smsErr) throw smsErr;
      await supabase.from("activity_log").insert({
        deal_id: x.row.id,
        activity_type: "ar_contact",
        title: "Final demand notice sent",
        description: `Final demand SMS sent to ${x.row.homeowner_name ?? "homeowner"} — balance ${fmtUSD(x.owed)} (${x.days}d past due).`,
        metadata: { by: member?.full_name ?? null, tag: "ar_final_demand" },
      });
      showToast("✓ Final demand sent");
    } catch {
      showToast("Failed to send — check phone number");
    } finally {
      setDemandBusy(null);
    }
  }

  async function submitLogContact() {
    if (!logTarget || !contactNote.trim()) return;
    setLogBusy(true);
    try {
      await supabase.from("activity_log").insert({
        deal_id: logTarget.id,
        activity_type: "ar_contact",
        title: "AR contact logged",
        description: contactNote.trim(),
        metadata: { by: member?.full_name ?? null },
      });
      showToast("✓ Contact logged");
      setLogTarget(null);
      setContactNote("");
    } catch {
      showToast("Failed to log contact");
    } finally {
      setLogBusy(false);
    }
  }

  // ── AR Report PDF — Shannon's weekly print-and-mark-up report (May 13, 2026) ──
  // Variant C from ar-report-prototype.html: bucketed by aging with notes column.
  // Pulls richer fields than what's in `rows` (email, alt phone, city/state,
  // carrier) and the owner_id → full_name map so the printed page has full
  // contact + assignment context.
  const [arPdfBusy, setArPdfBusy] = useState(false);
  async function generateArReport() {
    if (arPdfBusy) return;
    setArPdfBusy(true);
    try {
      const ids = aged.map((x) => x.row.id);
      if (ids.length === 0) {
        showToast("No open AR balances to report on");
        return;
      }
      // Pull richer fields than the AR tab's lean SELECT — needed for the printed
      // report's contact column and carrier line.
      const { data: rich, error: richErr } = await supabase
        .from("deals")
        .select("id,homeowner_name,homeowner_phone,homeowner_phone_alt,homeowner_email,property_street,property_city,property_state,insurance_company,claim_number,stage,owner_id,split_rm_id,final_invoice_amount,final_payment_paid,pre_build_amount_received,post_build_amount_received,balance_written_off")
        .in("id", ids);
      if (richErr) throw richErr;

      const ownerIds = Array.from(
        new Set(
          (rich ?? [])
            .flatMap((d) => [d.owner_id, d.split_rm_id])
            .filter((v): v is string => !!v),
        ),
      );
      const ownerNames: Record<string, string> = {};
      if (ownerIds.length > 0) {
        const { data: roster, error: rosterErr } = await supabase
          .from("team_roster")
          .select("id,full_name")
          .in("id", ownerIds);
        if (rosterErr) throw rosterErr;
        for (const m of roster ?? []) {
          if (m.id && m.full_name) ownerNames[m.id] = m.full_name;
        }
      }

      // Stitch rich rows back onto the bucketed `aged` array — keeps the
      // bucket/days/owed numbers identical to what's on screen.
      const richById = new Map((rich ?? []).map((d) => [d.id, d]));
      const reportRows = aged
        .map((a) => {
          const d = richById.get(a.row.id);
          if (!d) return null;
          const contract = Number(d.final_invoice_amount) || 0;
          const received = arPaidAgainst(d);
          return {
            id: d.id,
            homeowner_name: d.homeowner_name,
            homeowner_phone: d.homeowner_phone,
            homeowner_phone_alt: d.homeowner_phone_alt ?? null,
            homeowner_email: d.homeowner_email,
            property_street: d.property_street,
            property_city: d.property_city,
            property_state: d.property_state,
            insurance_company: d.insurance_company,
            claim_number: d.claim_number,
            stage: d.stage,
            owner_id: d.owner_id,
            split_rm_id: d.split_rm_id ?? null,
            contract,
            received,
            owed: a.owed,
            days: a.days,
            bucket: a.bucket,
          };
        })
        .filter((r): r is NonNullable<typeof r> => r !== null);

      const { generateArAgingReportPDF } = await import("../../lib/arAgingReportPDF");
      const now = new Date();
      const blob = await generateArAgingReportPDF({
        rows: reportRows,
        ownerNames,
        generatedAt: now,
        preparedFor: member?.full_name ? `${member.full_name} · Finance` : "Finance",
      });
      const url = URL.createObjectURL(blob);
      const stamp = now.toISOString().slice(0, 10);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Bardin-AR-Aging-${stamp}.pdf`;
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      showToast(`✓ AR report generated (${reportRows.length} accounts)`);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Unknown error";
      showToast(`Couldn't generate AR report — ${msg}`);
    } finally {
      setArPdfBusy(false);
    }
  }

  if (err) return <Panel error>Couldn't load receivables — {err}</Panel>;
  if (!rows) return <Panel>Loading receivables…</Panel>;

  return (
    <div className="space-y-4">
      {/* Toast */}
      {toast && (
        <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-lg bg-bardin-ink text-white text-sm font-medium shadow-lg">
          {toast}
        </div>
      )}

      {/* AR Report toolbar — Shannon's weekly print-and-mark-up button */}
      <div className="flex items-center justify-between gap-3 -mt-1">
        <div className="text-xs text-slate-500">
          {aged.length} open account{aged.length === 1 ? "" : "s"} ·{" "}
          <span className="font-semibold text-bardin-ink">
            {fmtUSD(aged.reduce((s, x) => s + x.owed, 0))}
          </span>{" "}
          outstanding
          {needsInvoice.length > 0 && (
            <span className="text-amber-700 font-medium">
              {" "}· {needsInvoice.length} awaiting invoice
            </span>
          )}
        </div>
        <button
          onClick={generateArReport}
          disabled={arPdfBusy || aged.length === 0}
          className={
            "inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold rounded-lg " +
            "bg-bardin-orange text-white shadow-sm hover:opacity-90 " +
            "disabled:opacity-40 disabled:cursor-not-allowed transition"
          }
          title="Generate a printable AR aging report with room for handwritten notes"
        >
          {arPdfBusy ? (
            <>
              <span className="inline-block w-3 h-3 rounded-full border-2 border-white/40 border-t-white animate-spin" />
              Generating…
            </>
          ) : (
            <>📄 AR Report</>
          )}
        </button>
      </div>

      {/* Log Contact modal */}
      {logTarget && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-5">
            <h3 className="text-sm font-semibold text-bardin-ink mb-1">
              Log Contact — {logTarget.name}
            </h3>
            <p className="text-xs text-slate-500 mb-3">
              What happened? (call outcome, voicemail, email, in-person, etc.)
            </p>
            <textarea
              autoFocus
              value={contactNote}
              onChange={(e) => setContactNote(e.target.value)}
              rows={3}
              placeholder="e.g. Left voicemail, HO said check is in the mail…"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-bardin-orange/40"
            />
            <div className="flex gap-2 mt-3 justify-end">
              <button
                onClick={() => { setLogTarget(null); setContactNote(""); }}
                className="px-3 py-1.5 text-xs rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={submitLogContact}
                disabled={logBusy || !contactNote.trim()}
                className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-bardin-orange text-white hover:opacity-90 disabled:opacity-40"
              >
                {logBusy ? "Saving…" : "Save note"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation modal — shown before any outbound SMS fires */}
      {confirmPending && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-5">
            <h3 className="text-sm font-semibold text-bardin-ink mb-0.5">
              {confirmPending.type === "demand" ? "📨 Send Final Demand" : "📱 Text Payment Link"}
            </h3>
            <p className="text-xs text-slate-500 mb-3">
              To: <span className="font-medium text-bardin-ink">{confirmPending.x.row.homeowner_name ?? "homeowner"}</span>{" "}
              · {confirmPending.phone}
            </p>
            {/* Message preview — exactly what will be sent */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-xs text-slate-700 leading-relaxed mb-4">
              {confirmPending.body}
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setConfirmPending(null)}
                className="px-3 py-1.5 text-xs rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  const p = confirmPending;
                  setConfirmPending(null);
                  if (p.type === "demand") {
                    await sendFinalDemand(p.x, p.body);
                  } else {
                    await sendPaymentLink(p.x, p.body);
                  }
                }}
                disabled={demandBusy === confirmPending.x.row.id || textBusy === confirmPending.x.row.id}
                className={
                  "px-3 py-1.5 text-xs font-semibold rounded-lg text-white hover:opacity-90 disabled:opacity-40 " +
                  (confirmPending.type === "demand" ? "bg-red-600" : "bg-bardin-orange")
                }
              >
                Send
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bucket summary cards — clickable to filter the lists below */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {BUCKETS.map((b) => {
          const isActive = activeBucket === b.key;
          const hasAlert = (b.key === "black" && bucketTotals.black.count > 0) || (b.key === "red" && bucketTotals.red.count > 0);
          return (
            <button
              key={b.key}
              onClick={() => setActiveBucket(isActive ? null : b.key)}
              className={
                "bg-white rounded-xl shadow p-4 text-left w-full transition-all hover:shadow-md " +
                (isActive
                  ? "ring-2 ring-bardin-orange ring-offset-1"
                  : hasAlert
                  ? b.key === "black" ? "ring-2 ring-black" : "ring-2 ring-red-400"
                  : "")
              }
            >
              <div className="flex items-center justify-between gap-1">
                <div className={"text-xs font-medium px-2 py-0.5 rounded inline-block " + b.class}>
                  {b.label}
                </div>
                {isActive && <span className="text-[10px] font-bold text-bardin-orange">filtered ✕</span>}
              </div>
              <div className="text-xl font-semibold text-bardin-ink mt-2">
                {fmtUSD(bucketTotals[b.key].value, { short: true })}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {fmtInt(bucketTotals[b.key].count)} deals
              </div>
            </button>
          );
        })}
      </div>
      {activeBucket && (
        <div className="flex items-center gap-2 text-xs text-bardin-orange font-medium -mt-1">
          <span>Showing {BUCKETS.find((b) => b.key === activeBucket)?.label} deals only</span>
          <button onClick={() => setActiveBucket(null)} className="underline">Clear filter</button>
        </div>
      )}

      {/* Awaiting invoice — collecting/delinquent deals with no final invoice
          amount entered. They compute to $0 owed and drop out of the aging
          buckets below, so they're surfaced here for finance to enter the
          invoice and begin aging. Not affected by the bucket filter. */}
      {needsInvoice.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-base">🧾</span>
            <span className="text-sm font-semibold text-amber-800">
              {needsInvoice.length} deal{needsInvoice.length > 1 ? "s" : ""} awaiting invoice
            </span>
          </div>
          <p className="text-xs text-amber-700 mb-2">
            In collections but no final invoice amount entered — enter the invoice on the deal to begin aging &amp; collecting.
          </p>
          <ul className="space-y-1">
            {needsInvoice.map((r) => (
              <li key={r.id} className="text-xs text-amber-800 flex items-center gap-2 flex-wrap">
                <span className="px-1.5 py-0.5 rounded font-bold bg-amber-200 text-amber-900">
                  {(r.stage ?? "").replace(/_/g, " ")}
                </span>
                <DealLink id={r.id} className="font-semibold hover:underline">
                  {r.homeowner_name ?? "(no name)"}
                </DealLink>
                {r.property_street && <span className="text-amber-600">{r.property_street}</span>}
                {r.claim_number && <span className="text-amber-500">#{r.claim_number}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Escalation callout */}
      {escalations.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-base">⚠️</span>
            <span className="text-sm font-semibold text-red-700">
              {escalations.length} deal{escalations.length > 1 ? "s" : ""} need escalation
            </span>
          </div>
          <ul className="space-y-1">
            {escalations.map((x) => {
              const bkt = BUCKETS.find((b) => b.key === x.bucket)!;
              return (
                <li key={x.row.id} className="text-xs text-red-700 flex items-center gap-2">
                  <span className={"px-1.5 py-0.5 rounded font-bold " + bkt.class}>{x.days}d</span>
                  <DealLink id={x.row.id} className="font-semibold hover:underline">
                    {x.row.homeowner_name ?? "(no name)"}
                  </DealLink>
                  {x.row.property_street && <span className="text-red-500">{x.row.property_street}</span>}
                  <span>— {fmtUSD(x.owed, { short: true })} balance</span>
                  {x.bucket === "black" && <span className="font-bold">· Warranty at risk</span>}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Action items — yellow and worse, filtered when a bucket card is active */}
      {(() => {
        const visibleItems = activeBucket
          ? actionItems.filter((x) => x.bucket === activeBucket)
          : actionItems;
        if (visibleItems.length === 0) return null;
        return (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-bardin-ink px-0.5">
            Action items ({visibleItems.length}{actionItems.length !== visibleItems.length ? ` of ${actionItems.length}` : ""})
          </h3>
          {visibleItems.map((x) => {
            const bkt = BUCKETS.find((b) => b.key === x.bucket)!;
            const isCritical = x.bucket === "red" || x.bucket === "black";
            const cardBorder = x.bucket === "black"
              ? "border-black bg-slate-50"
              : x.bucket === "red"
              ? "border-red-300 bg-red-50/30"
              : x.bucket === "orange"
              ? "border-orange-200 bg-orange-50/20"
              : "border-yellow-200 bg-yellow-50/20";
            return (
              <div key={x.row.id} className={`rounded-xl border-2 p-4 ${cardBorder}`}>
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div>
                    <div className="font-semibold text-sm text-bardin-ink">
                      <DealLink id={x.row.id}>{x.row.homeowner_name ?? "(no name)"}</DealLink>
                      {x.row.property_street && (
                        <span className="font-normal text-slate-500 ml-1">· {x.row.property_street}</span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {x.row.claim_number ? `#${x.row.claim_number} · ` : ""}
                      {(x.row.stage ?? "").replace(/_/g, " ")}
                      {!x.row.homeowner_phone && (
                        <span className="ml-2 text-amber-600 font-medium">⚠ no phone on file</span>
                      )}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="font-bold text-sm text-bardin-ink">{fmtUSD(x.owed, { short: true })}</div>
                    <span className={"text-[10px] font-semibold px-2 py-0.5 rounded " + bkt.class}>
                      {x.days}d · {bkt.label}
                    </span>
                  </div>
                </div>
                {/* Action buttons */}
                <div className="flex flex-wrap gap-2 mt-3">
                  {isCritical && (
                    <button
                      onClick={() => openConfirm("demand", x)}
                      disabled={demandBusy === x.row.id}
                      className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 flex items-center gap-1"
                    >
                      {demandBusy === x.row.id ? "Sending…" : "📨 Send Final Demand"}
                    </button>
                  )}
                  <button
                    onClick={() => openConfirm("text", x)}
                    disabled={textBusy === x.row.id}
                    className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-bardin-orange text-white hover:opacity-90 disabled:opacity-50 flex items-center gap-1"
                  >
                    {textBusy === x.row.id ? "Sending…" : "📱 Text Payment Link"}
                  </button>
                  <button
                    onClick={() => {
                      setLogTarget({ id: x.row.id, name: x.row.homeowner_name ?? "(no name)" });
                      setContactNote("");
                    }}
                    className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                  >
                    📝 Log Contact
                  </button>
                  <ReconcileButton dealId={x.row.id} />
                </div>
              </div>
            );
          })}
        </div>
        );
      })()}

      {/* Full open receivables list — also respects the active bucket filter */}
      {(() => {
        const visibleAged = activeBucket ? aged.filter((x) => x.bucket === activeBucket) : aged;
        const visibleTotal = visibleAged.reduce((s, x) => s + x.owed, 0);
        return (
          <Panel>
            <div className="flex items-baseline justify-between mb-3">
              <h3 className="text-sm font-semibold text-bardin-ink">All open receivables</h3>
              <span className="text-xs text-slate-500">
                {fmtUSD(visibleTotal, { short: true })} · {visibleAged.length} deal{visibleAged.length !== 1 ? "s" : ""}
                {activeBucket && aged.length !== visibleAged.length ? ` (filtered from ${aged.length})` : ""}
              </span>
            </div>
            {visibleAged.length === 0 ? (
              <Empty>No open receivables{activeBucket ? " in this bucket" : ""}.</Empty>
            ) : (
              <ul className="divide-y divide-slate-100">
                {visibleAged.map((x) => {
                  const bucket = BUCKETS.find((b) => b.key === x.bucket)!;
                  return (
                    <li key={x.row.id} className="py-2 text-sm flex items-center justify-between">
                      <div>
                        <div className="font-medium text-bardin-ink">
                          <DealLink id={x.row.id}>{x.row.homeowner_name ?? "(no name)"}</DealLink>
                        </div>
                        <div className="text-xs text-slate-500">
                          {x.row.claim_number ? `#${x.row.claim_number} · ` : ""}
                          {x.row.property_street ? `${x.row.property_street} · ` : ""}
                          {(x.row.stage ?? "").replace(/_/g, " ")}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <div className="font-semibold">{fmtUSD(x.owed, { short: true })}</div>
                          <div className="text-xs text-slate-400">{x.days}d</div>
                        </div>
                        <span className={"text-xs px-2 py-0.5 rounded " + bucket.class}>
                          {bucket.label}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </Panel>
        );
      })()}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shannon's Payments tab — Ship 4 v1
// 6-bucket strip from the prototype: deposit needed / clear needed / Stripe
// pending / push to QB / drift / matched. Push-to-QB and matched/drift
// populate properly after Ship 8 (QBO sync); for Ship 4 v1 they stay empty
// or pull from existing CRM-side state. CheckTrackerCard renders each row.
// ---------------------------------------------------------------------------

type DealContext = {
  id: string;
  homeowner_name: string | null;
  homeowner_phone: string | null;
  property_street: string | null;
  final_invoice_amount: number | null;
  final_payment_paid: number | null;
};

function ShannonPaymentsTab() {
  const [payments, setPayments] = useState<DealPayment[] | null>(null);
  const [dealMap, setDealMap] = useState<Record<string, DealContext>>({});
  const [err, setErr] = useState<string | null>(null);
  // Default bucket is "stuck_iink" — Shannon lands on the urgent thing first
  // when there are stuck checks, otherwise falls through to the in-workflow view.
  const [bucket, setBucket] = useState<string>("stuck_iink");
  const [editing, setEditing] = useState<DealPayment | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => setReloadKey((k) => k + 1), []);
  useRealtimeTable("deal_payments", reload);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const { data, error } = await supabase
        .from("deal_payments")
        .select("*")
        .not("state", "in", "(bank_cleared,voided,refunded)")
        .order("created_at", { ascending: false })
        .limit(200);
      if (error) {
        if (!cancelled) setErr(error.message);
        return;
      }
      const paymentsLoaded = (data ?? []) as DealPayment[];
      if (!cancelled) setPayments(paymentsLoaded);

      // Second query: join deal context (homeowner, phone, address, invoice amounts)
      // so CheckTrackerCard can show the HO name + Text Payment Link.
      const dealIds = [...new Set(paymentsLoaded.map((p) => p.deal_id).filter(Boolean))] as string[];
      if (dealIds.length === 0 || cancelled) return;
      const { data: dealRows } = await supabase
        .from("deals")
        .select("id,homeowner_name,homeowner_phone,property_street,final_invoice_amount,final_payment_paid")
        .in("id", dealIds);
      if (!cancelled && dealRows) {
        const map: Record<string, DealContext> = {};
        for (const d of dealRows as DealContext[]) map[d.id] = d;
        setDealMap(map);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [reloadKey]);

  if (err) return <Panel error>Couldn't load payments — {err}</Panel>;
  if (!payments) return <Panel>Loading payments…</Panel>;

  // Bucket the in-flight payments. iink path is split into its own bucket
  // (separate from non-iink checks); stuck iink is an overlapping ALERT subset
  // of IINK Submitted (Greg, Aug 4 2026: the submitted card shows ALL iink checks).
  const inFlightStates = new Set(["collected", "iink_submitted", "mortgage_endorsed", "fedex_out", "fedex_back", "iink_cleared"]);
  const iinkLifecycleStates = new Set(["iink_submitted", "mortgage_endorsed", "fedex_out", "fedex_back", "iink_cleared"]);
  const STUCK_DAYS = 10;  // matches finance_settings.iink_stuck_alert_days default

  function daysInState(p: DealPayment): number {
    const ts = ({
      collected: p.collected_at,
      iink_submitted: p.iink_submitted_at,
      mortgage_endorsed: p.mortgage_endorsed_at,
      fedex_out: p.fedex_out_at,
      fedex_back: p.fedex_back_at,
      iink_cleared: p.iink_cleared_at,
    } as Record<string, string | null>)[p.state];
    if (!ts) return 0;
    return Math.floor((Date.now() - new Date(ts).getTime()) / 86_400_000);
  }

  // Categorize each in-flight payment. IINK Submitted holds EVERY iink-path
  // check in flight; Stuck in iink is the ≥10d subset of it (overlaps by design).
  const stuckIink: DealPayment[]     = [];   // iink path + ≥10d in current state — chase list
  const iinkSubmitted: DealPayment[] = [];   // ALL iink-path checks in flight (includes stuck)
  const otherChecks: DealPayment[]   = [];   // direct path — no iink (HO personal, direct-to-contractor)
  for (const p of payments) {
    if (!inFlightStates.has(p.state)) continue;
    if (p.method !== "check") continue;  // shouldn't be here, defensive
    // iink path = flagged iink_required OR already walked into an iink lifecycle
    // state (rows can carry iink_required=false yet sit at iink_submitted — trust the state)
    if (p.iink_required || iinkLifecycleStates.has(p.state)) {
      iinkSubmitted.push(p);
      if (daysInState(p) >= STUCK_DAYS) stuckIink.push(p);
    } else {
      otherChecks.push(p);
    }
  }
  const clearNeeded     = payments.filter((p) => p.state === "deposited");
  const stripePending   = payments.filter((p) => p.state === "made" && p.method === "stripe");
  // Stripe ACH typed in by hand, waiting on the homeowner's bank verification
  // (Oct 20, 2026): these won't clear on their own.
  const awaitingVerify  = stripePending.filter((p) => p.ach_verify_status === "pending").length;
  const pushToQB        = payments.filter((p) => p.state === "cleared" && !p.qb_payment_id);
  const disputed        = payments.filter((p) => p.state === "disputed");

  const buckets = [
    { key: "stuck_iink",      icon: "🚨", label: "Stuck in iink",  sub: "≥10d at iink stage — chase",  count: stuckIink.length,    cls: stuckIink.length > 0 ? "border-red-500 bg-red-50/40 ring-2 ring-red-200" : "border-slate-200" },
    { key: "iink_in_flight",  icon: "🏛", label: "IINK Submitted", sub: "All checks with iink — mortgage endorsement path", count: iinkSubmitted.length, cls: "border-purple-300 bg-purple-50/30" },
    { key: "other_checks",    icon: "🔴", label: "Direct checks",   sub: "No iink — HO personal / direct-to-contractor", count: otherChecks.length, cls: "border-amber-300 bg-amber-50/30" },
    { key: "clear_needed",    icon: "🟡", label: "Clear needed",   sub: "Confirm in QuickBooks",       count: clearNeeded.length,  cls: "border-amber-300 bg-amber-50/30" },
    { key: "stripe_pending",  icon: "↻",  label: "Stripe pending", sub: awaitingVerify > 0 ? `${awaitingVerify} waiting on the homeowner's bank verification` : "Auto-clearing in ~2d", count: stripePending.length, cls: "border-blue-300 bg-blue-50/30" },
    { key: "disputed",        icon: "🆘", label: "Disputed",       sub: "Stripe chargebacks",          count: disputed.length,     cls: "border-red-400 bg-red-50/30" },
    { key: "push_to_qb",      icon: "⚠",  label: "Push to QB",     sub: "Cleared in CRM, not in QB",   count: pushToQB.length,     cls: "border-amber-300 bg-amber-50/30" },
    { key: "matched",         icon: "✅", label: "In sync",        sub: "QBO reconciliation (Ship 8.5)", count: 0,                  cls: "border-emerald-300 bg-emerald-50/30" },
  ];

  const visible = ({
    stuck_iink:     stuckIink,
    iink_in_flight: iinkSubmitted,
    other_checks:   otherChecks,
    clear_needed:   clearNeeded,
    stripe_pending: stripePending,
    push_to_qb:     pushToQB,
    disputed,
    matched: [] as DealPayment[],
  } as Record<string, DealPayment[]>)[bucket] ?? [];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
        {buckets.map((b) => (
          <button
            key={b.key}
            onClick={() => setBucket(b.key)}
            className={
              "rounded-xl border-2 p-3 text-left transition-all hover:-translate-y-0.5 " +
              (bucket === b.key ? b.cls : "border-slate-200 bg-white")
            }
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-base">{b.icon}</span>
              <span className="text-xl font-extrabold text-bardin-ink">{b.count}</span>
            </div>
            <div className="text-xs font-bold text-bardin-ink">{b.label}</div>
            <div className="text-[10px] text-slate-500 mt-0.5">{b.sub}</div>
          </button>
        ))}
      </div>

      <div className="space-y-2">
        {visible.length === 0 ? (
          <Panel>
            <Empty>
              {bucket === "matched"
                ? "QBO reconciliation populates after Ship 8 wires the QuickBooks sync."
                : bucket === "disputed"
                ? "🎉 No active Stripe disputes."
                : "Nothing in this bucket. 🎉"}
            </Empty>
          </Panel>
        ) : (
          visible.map((p) => {
            const deal = p.deal_id ? dealMap[p.deal_id] : undefined;
            // Infer amount owed from the deal's invoice vs paid, fallback to payment amount
            const amountOwed =
              deal?.final_invoice_amount != null
                ? Math.max(0, Number(deal.final_invoice_amount) - (Number(deal.final_payment_paid) || 0))
                : Number(p.amount) || undefined;
            // Ship 12: disputed payments get a dedicated action card with
            // Respond / Refund / Escalate buttons and Stripe deadline visibility.
            // All other states render the standard CheckTrackerCard.
            const card = bucket === "disputed" ? (
              <DisputedPaymentCard payment={p} onResolved={reload} />
            ) : (
              <CheckTrackerCard
                payment={p}
                onAdvanced={reload}
                onIinkToggled={reload}
                dealId={p.deal_id ?? undefined}
                homeownerName={deal?.homeowner_name}
                homeownerPhone={deal?.homeowner_phone}
                street={deal?.property_street}
                amountOwed={amountOwed}
                canManage
                onEdit={() => setEditing(p)}
                onDeleted={reload}
              />
            );
            return (
              <div key={p.id} className="space-y-1">
                {card}
                {p.deal_id && (
                  <div className="flex justify-end pr-1">
                    <ReconcileButton dealId={p.deal_id} size="xs" />
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {editing && (
        <PaymentEditModal
          payment={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { reload(); setEditing(null); }}
        />
      )}
    </div>
  );
}

// (Legacy 30-day-receipts list removed in Ship 4 — ShannonPaymentsTab above
//  is the new payment surface, driven by deal_payments.)

// ---------------------------------------------------------------------------
// Supplements — with Apex tracker + insurance SLA
// ---------------------------------------------------------------------------

// Ship 2 (May 2, 2026 — Finance Board Redesign):
// SupplementsTab now renders deals grouped by their supplement-stage in
// chronological pipeline order (ready_for_supps → … → register_oc_warranty).
// Each card shows Chad's notes preview + the next-stage transition button,
// with SLA tints on aging stages. Lifecycle filter bar + inline edit + payment
// history come in Ship 11 polish; this is the v1 functional layout.
//
// Polish-deferred features (see prototypes/finance-board-prototype.html):
//   - clickable lifecycle bar to filter by stage
//   - inline-editable supplement_invoice_amount / supplement_approved_amount
//   - payment history block on collecting/delinquent/register_oc cards
//   - search bar + filter chips
const SUPP_STAGE_GROUPS: Array<{ key: string; label: string; dotClass: string; tintClass: string }> = [
  { key: "ready_for_supps",                  label: "Ready for supps",            dotClass: "bg-slate-400",   tintClass: "bg-slate-50" },
  { key: "ready_for_updated_invoice",        label: "Ready for updated invoice",  dotClass: "bg-amber-500",   tintClass: "bg-amber-50/40" },
  { key: "supps_waiting_on_final_invoice",   label: "Supps waiting on final invoice", dotClass: "bg-teal-500", tintClass: "bg-teal-50/40" },
  { key: "supps_waiting_on_claims",          label: "Supps waiting on final invoice (legacy)", dotClass: "bg-teal-500", tintClass: "bg-teal-50/40" },
  { key: "collecting",                       label: "Collecting $",               dotClass: "bg-emerald-500", tintClass: "bg-emerald-50/40" },
  { key: "delinquent",                       label: "Delinquent",                 dotClass: "bg-red-500",     tintClass: "bg-red-50/40" },
  { key: "register_oc_warranty",             label: "Register OC warranty",       dotClass: "bg-purple-500",  tintClass: "bg-purple-50/40" },
  { key: "closed_won",                       label: "Complete (Closed Won)",      dotClass: "bg-slate-700",   tintClass: "bg-slate-50" },
];

const SUPP_STAGES = SUPP_STAGE_GROUPS.map((g) => g.key);

// Open (still-in-flight) supps stages = every lifecycle stage EXCEPT the final
// Complete bucket. closed_won is queried separately and scoped to finance-flow
// deals only (commission_ready_date set) so the ~497 legacy pre-system closes
// never flood this board — only the ~36 jobs that actually ran through supps.
const SUPP_OPEN_STAGES = SUPP_STAGES.filter((s) => s !== "closed_won");

// Pre-collecting supps stages where a commission can already be pending (the
// commission gate opens at ready_for_supps) but the deal hasn't reached
// collecting yet. On these stages the "write off remaining balance" action is
// unsafe — post_build_balance_remaining is the full uncollected final-invoice
// amount, not a small residual — so we surface a separate TARGETED residual
// write-off (enter an amount, no stage advance) to clear a short first-payment
// shortfall that's blocking commission approval. Jul 1, 2026.
const SUPP_RESIDUAL_STAGES = new Set<string>([
  "ready_for_supps",
  "ready_for_updated_invoice",
  "supps_waiting_on_final_invoice",
  "supps_waiting_on_claims",
]);

// All filterable lifecycle stage keys (excludes the deprecated alias). When
// the URL `?supp=<key>` is set, SupplementsTab shows only the matching group.
const SUPP_FILTERABLE_KEYS = [
  "ready_for_supps",
  "ready_for_updated_invoice",
  "supps_waiting_on_final_invoice",
  "collecting",
  "delinquent",
  "register_oc_warranty",
  "closed_won",
] as const;
type SuppStageFilter = (typeof SUPP_FILTERABLE_KEYS)[number] | "all";

// `?comms=needs_reply` narrows every group to the cards whose newest text is an
// unanswered homeowner reply (Oct 16, 2026). Namespaced like `supp`.
const SUPP_COMMS_FILTERS = ["all", "needs_reply"] as const;
type SuppCommsFilter = (typeof SUPP_COMMS_FILTERS)[number];

// ── The supps-sent text on each card (Oct 12 + Oct 20, 2026) ─────────────────
// One read per board: the latest supplement_sent text per deal (the "Texted ✓" chip), plus what the automatic
// version (homeowner-comms touch t6) is doing: the engine switches and each deal's t6 row. Each part fails on its
// own; while the engine part has never loaded, the cards keep the plain button.
async function fetchSuppsTextState(allIds: string[], textIds: string[]): Promise<{
  textedAt: Record<string, string> | null;
  env: AutoTextEnv | null;
  t6: Record<string, AutoTextRow> | null;
}> {
  const texted = async (): Promise<Record<string, string> | null> => {
    const { data, error } = await supabase
      .from("twilio_messages")
      .select("deal_id,sent_at,created_at")
      .eq("tag", SUPPLEMENT_SENT_TAG)
      .eq("direction", "outbound")
      .in("deal_id", allIds)
      .order("created_at", { ascending: true });
    if (error || !data) return null;
    const m: Record<string, string> = {};
    for (const t of data as { deal_id: string; sent_at: string | null; created_at: string }[]) m[t.deal_id] = t.sent_at ?? t.created_at;
    return m;
  };
  const engine = async (): Promise<AutoTextEnv | null> => {
    const { data: settings, error } = await supabase
      .from("finance_settings")
      .select("key,value_jsonb")
      .in("key", ["hc_mode", "hc_disabled", "hc_test_deal_ids"]);
    if (error || !settings) return null;
    let muted: string[] = [];
    if (textIds.length) {
      const { data: mutes, error: muteErr } = await supabase
        .from("deal_comm_settings")
        .select("deal_id")
        .eq("mute_all", true)
        .in("deal_id", textIds);
      if (muteErr) return null;
      muted = ((mutes ?? []) as { deal_id: string }[]).map((m) => m.deal_id);
    }
    const val = (k: string): unknown => (settings as { key: string; value_jsonb: unknown }[]).find((s) => s.key === k)?.value_jsonb;
    const list = (k: string): string[] => {
      const v = val(k);
      return Array.isArray(v) ? v.map(String) : [];
    };
    const mode = val("hc_mode");
    return {
      mode: typeof mode === "string" ? mode : "off",
      testDealIds: new Set(list("hc_test_deal_ids")),
      disabled: new Set(list("hc_disabled")),
      muted: new Set(muted),
    };
  };
  const t6 = async (): Promise<Record<string, AutoTextRow> | null> => {
    if (!textIds.length) return {};
    const { data, error } = await supabase
      .from("homeowner_comm_sends")
      .select("deal_id,status,skip_reason,sms_status,scheduled_for,sent_at")
      .eq("comm_key", "t6")
      .in("deal_id", textIds);
    if (error || !data) return null;
    const m: Record<string, AutoTextRow> = {};
    for (const r of data as (AutoTextRow & { deal_id: string })[]) m[r.deal_id] = r;
    return m;
  };
  const [textedAt, env, t6Map] = await Promise.all([texted(), engine(), t6()]);
  return { textedAt, env, t6: t6Map };
}

// ── Auto-remind on the collecting / delinquent cards (Oct 20, 2026) ──────────
// The read-only chip ("Auto-remind on · next Mon, Oct 26"); the switch itself is on the Send Invoice
// panel. One read per board; a failure leaves the chips off.
interface AutoRemindCard {
  settings: ReminderSettings;
  anchorSentAt: string | null;
  rows: ReminderRow[];
}

async function fetchAutoRemindCards(ids: string[]): Promise<Record<string, AutoRemindCard> | null> {
  if (!ids.length) return {};
  const { data: cs, error } = await supabase
    .from("deal_comm_settings")
    .select("deal_id,auto_remind,auto_remind_anchor_delivery_id,auto_remind_enabled_by,auto_remind_enabled_at,auto_remind_paused_at,auto_remind_stopped_at,auto_remind_stop_reason")
    .in("deal_id", ids)
    .or("auto_remind.eq.true,auto_remind_stopped_at.not.is.null");
  if (error || !cs) return null;
  const list = cs as (ReminderSettings & { deal_id: string })[];
  if (!list.length) return {};
  const anchorIds = list.map((s) => s.auto_remind_anchor_delivery_id).filter((x): x is string => !!x);
  const [anchors, t8] = await Promise.all([
    anchorIds.length
      ? supabase.from("deal_invoice_deliveries").select("id,sent_at").in("id", anchorIds)
      : Promise.resolve({ data: [] as { id: string; sent_at: string }[], error: null }),
    supabase
      .from("homeowner_comm_sends")
      .select("deal_id,ref,status,skip_reason,sent_at,scheduled_for,created_at")
      .eq("comm_key", "t8")
      .in("deal_id", list.map((s) => s.deal_id)),
  ]);
  if (anchors.error || t8.error) return null;
  const sentAt = new Map(((anchors.data ?? []) as { id: string; sent_at: string }[]).map((a) => [a.id, a.sent_at]));
  const out: Record<string, AutoRemindCard> = {};
  for (const s of list) {
    out[s.deal_id] = {
      settings: s,
      anchorSentAt: s.auto_remind_anchor_delivery_id ? sentAt.get(s.auto_remind_anchor_delivery_id) ?? null : null,
      rows: ((t8.data ?? []) as (ReminderRow & { deal_id: string })[]).filter((r) => r.deal_id === s.deal_id),
    };
  }
  return out;
}

/** "10:25 AM" today, "Tue 8:00 AM" on another day. */
function fmtAutoEta(d: Date): string {
  return d.toDateString() === new Date().toDateString()
    ? d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
    : d.toLocaleString("en-US", { weekday: "short", hour: "numeric", minute: "2-digit" });
}

function SupplementsTab() {
  type Row = Pick<
    Deal,
    | "id"
    | "homeowner_name"
    | "homeowner_email"
    | "homeowner_phone"
    | "claim_number"
    | "stage"
    | "owner_id"
    | "split_rm_id"
    | "build_completed_date"
    | "days_in_stage"
    | "stage_entered_at"
    | "phase_entered_at"
  | "phase_entered_at"
    | "created_at"
    | "chad_supplement_notes"
    | "supplement_invoice_amount"
    | "supplement_approved_amount"
    | "rcv_total"
    | "supplement_submitted_to_insurance_date"
    | "supplement_approved_date"
    | "supplement_estimate_received_date"
    | "supplement_invoice_pdf_url"
    | "supplement_invoice_generated_at"
    | "supplement_invoice_number"
    | "supplement_invoice_pdf_amount"
    | "supplement_invoice_pdf_received"
    | "supplement_invoice_pdf_pending"
    | "final_invoice_amount"
    | "initial_invoice_amount"
    | "invoice_override_amount"
    | "invoice_override_reason"
    | "final_payment_paid"
    | "post_build_balance_remaining"
    | "balance_written_off"
    | "pre_build_amount_received"
    | "post_build_amount_received"
    | "misc_credit"
    | "property_street"
    | "property_city"
    | "property_state"
    | "property_zip"
    | "insurance_company"
    | "warranty_registered_date"
    | "oc_warranty_certificate_uploaded"
    | "commission_ready_date"
  >;
  const [rows, setRows] = useState<Row[] | null>(null);
  // deal id → when the "supps sent" text last went out (twilio_messages tagged supplement_sent), Oct 12, 2026
  const [textedAt, setTextedAt] = useState<Record<string, string>>({});
  // The automatic version (Oct 20, 2026): the engine switches + each deal's t6 row. null = not loaded yet,
  // and the cards keep the plain button until it is.
  const [autoEnv, setAutoEnv] = useState<AutoTextEnv | null>(null);
  const [t6Rows, setT6Rows] = useState<Record<string, AutoTextRow> | null>(null);
  const suppsTextIdsRef = useRef<{ all: string[]; text: string[]; remind: string[] }>({ all: [], text: [], remind: [] });
  const suppsTextReqRef = useRef(0);
  // Auto-remind chips on collecting / delinquent cards (Oct 20, 2026); null = not loaded or the read failed.
  const [autoRemind, setAutoRemind] = useState<Record<string, AutoRemindCard> | null>(null);
  const refetchSuppsText = useCallback(async () => {
    const { all, text, remind } = suppsTextIdsRef.current;
    if (all.length === 0) return;
    const req = ++suppsTextReqRef.current;
    const [res, cards] = await Promise.all([fetchSuppsTextState(all, text), fetchAutoRemindCards(remind)]);
    if (req !== suppsTextReqRef.current) return; // a newer read is in flight
    // A failed part keeps its last good value, so a flaky read never flips a card back to the plain button.
    if (res.textedAt) setTextedAt(res.textedAt);
    if (res.env && res.t6) {
      setAutoEnv(res.env);
      setT6Rows(res.t6);
    }
    if (cards) setAutoRemind(cards);
  }, []);
  // Live: the engine queuing, sending or skipping a text updates the cards (debounced; a send writes a few updates).
  const suppsTextTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleSuppsTextRefetch = useCallback(() => {
    if (suppsTextTimerRef.current) clearTimeout(suppsTextTimerRef.current);
    suppsTextTimerRef.current = setTimeout(() => {
      suppsTextTimerRef.current = null;
      void refetchSuppsText();
    }, 800);
  }, [refetchSuppsText]);
  useRealtimeTable("homeowner_comm_sends", scheduleSuppsTextRefetch);
  useRealtimeTable("deal_comm_settings", scheduleSuppsTextRefetch);
  useEffect(() => () => { if (suppsTextTimerRef.current) clearTimeout(suppsTextTimerRef.current); }, []);
  const [err, setErr] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => setReloadKey((k) => k + 1), []);
  // Live-refresh when any deal changes stage (e.g. a deal transitions into
  // collecting) so the lifecycle groups update without a manual page reload.
  useRealtimeTable("deals", reload);
  // URL-backed lifecycle filter — keeps deep-link state and survives back-from-deal.
  // Param name `supp` is namespaced so it doesn't collide with the parent tab `?tab=`.
  const [stageFilter, setStageFilter] = useTabParam<SuppStageFilter>(
    "supp",
    "all",
    ["all", ...SUPP_FILTERABLE_KEYS] as readonly SuppStageFilter[],
  );

  // ── Comms on the board (Oct 16, 2026) ─────────────────────────────────────
  // Newest text + needs-reply flag per deal: one read per board (last 90 days),
  // refetched on its own when twilio_messages changes. null = not loaded or the
  // read failed → the strips stay hidden, the board stays up.
  const [commsMap, setCommsMap] = useState<CommsMap | null>(null);
  const commsIdsRef = useRef<string[]>([]);
  const commsReqRef = useRef(0);
  const refetchComms = useCallback(async () => {
    const ids = commsIdsRef.current;
    if (ids.length === 0) return;
    const req = ++commsReqRef.current;
    const map = await fetchCommsMap(ids);
    if (req !== commsReqRef.current) return; // a newer refetch is in flight
    if (map) setCommsMap(map); // a failed refetch keeps the last good map
  }, []);
  // Live: a homeowner reply or a sent text anywhere refreshes just the strips,
  // not the whole board. Debounced — an inbound text fires an INSERT plus status
  // UPDATEs within a second or two.
  const commsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleCommsRefetch = useCallback(() => {
    if (commsTimerRef.current) clearTimeout(commsTimerRef.current);
    commsTimerRef.current = setTimeout(() => {
      commsTimerRef.current = null;
      void refetchComms();
    }, 800);
  }, [refetchComms]);
  useRealtimeTable("twilio_messages", scheduleCommsRefetch);
  useEffect(() => () => { if (commsTimerRef.current) clearTimeout(commsTimerRef.current); }, []);
  // The reply drawer: one open thread at a time, keyed by deal id.
  const [openCommsId, setOpenCommsId] = useState<string | null>(null);
  const closeComms = useCallback(() => setOpenCommsId(null), []);
  // Team names for the thread ("by Shannon Ser"), loaded once the first drawer opens.
  const [members, setMembers] = useState<Record<string, TeamMember> | null>(null);
  useEffect(() => {
    if (!openCommsId || members !== null) return;
    let cancelled = false;
    supabase
      .from("team_roster")
      .select("*")
      .eq("is_active", true)
      .then(({ data }) => {
        if (cancelled) return;
        const byId: Record<string, TeamMember> = {};
        (data ?? []).forEach((x) => { byId[x.id] = x as TeamMember; });
        setMembers(byId);
      });
    return () => { cancelled = true; };
  }, [openCommsId, members]);
  const [commsFilter, setCommsFilter] = useTabParam<SuppCommsFilter>("comms", "all", SUPP_COMMS_FILTERS);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      // Oct 29, 2026: + trade_ho_total, trade_ho_lines, the trades billed to the
      // homeowner, for the invoice PDF's "Includes <trade>" lines (SendInvoicePanel
      // generates the PDF from this row).
      const { data, error } = await supabase
        .from("deals")
        .select(
          "id,homeowner_name,homeowner_email,homeowner_phone,claim_number,stage,owner_id,split_rm_id,build_completed_date,days_in_stage,stage_entered_at,phase_entered_at,created_at,chad_supplement_notes,supplement_invoice_amount,supplement_approved_amount,rcv_total,supplement_submitted_to_insurance_date,supplement_approved_date,supplement_estimate_received_date,supplement_invoice_pdf_url,supplement_invoice_generated_at,supplement_invoice_number,supplement_invoice_pdf_amount,supplement_invoice_pdf_received,supplement_invoice_pdf_pending,final_invoice_amount,initial_invoice_amount,invoice_override_amount,invoice_override_reason,final_payment_paid,post_build_balance_remaining,balance_written_off,pre_build_amount_received,post_build_amount_received,misc_credit,property_street,property_city,property_state,property_zip,insurance_company,warranty_registered_date,oc_warranty_certificate_uploaded,commission_ready_date,trade_ho_total,trade_ho_lines",
        )
        // Open supps stages, OR a closed_won deal that actually ran through the
        // finance flow (commission_ready_date set). The second clause keeps the
        // ~497 legacy pre-system closes out of the Complete bucket.
        .or(
          `stage.in.(${SUPP_OPEN_STAGES.join(",")}),and(stage.eq.closed_won,commission_ready_date.not.is.null)`,
        )
        .order("stage_entered_at", { ascending: true });
      if (error) {
        if (!cancelled) setErr(error.message);
        return;
      }
      if (!cancelled) setRows((data ?? []) as Row[]);
      const ids = ((data ?? []) as Row[]).map((r) => r.id);
      // Newest text per deal for the strips — its own read, so a failure hides the strips, never the board.
      if (!cancelled) {
        commsIdsRef.current = ids;
        void refetchComms();
      }
      // The "supps sent" text per deal (latest tagged message wins) and its automatic version. Its own read, so a
      // failure leaves the chips off and the plain button on, never the board.
      if (!cancelled) {
        suppsTextIdsRef.current = {
          all: ids,
          text: ((data ?? []) as Row[]).filter((r) => SUPPLEMENT_TEXT_STAGES.has(r.stage ?? "")).map((r) => r.id),
          remind: ((data ?? []) as Row[]).filter((r) => REMIND_STAGES.has(r.stage ?? "")).map((r) => r.id),
        };
        void refetchSuppsText();
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [reloadKey, refetchComms, refetchSuppsText]);

  if (err) return <Panel error>Couldn't load supplements — {err}</Panel>;
  if (!rows) return <Panel>Loading supplements…</Panel>;

  // KPIs
  const totalApproved = rows
    .filter((r) => r.supplement_approved_amount)
    .reduce((s, r) => s + (Number(r.supplement_approved_amount) || 0), 0);
  const inFlight = rows.filter((r) =>
    ["ready_for_supps", "ready_for_updated_invoice", "supps_waiting_on_final_invoice", "supps_waiting_on_claims"].includes(
      r.stage ?? "",
    ),
  );
  const collecting = rows.filter((r) => r.stage === "collecting" || r.stage === "delinquent");
  const delinquent = rows.filter((r) => r.stage === "delinquent");

  // Group by stage. Treat the deprecated alias as the same bucket as the new key.
  const grouped: Record<string, Row[]> = {};
  for (const g of SUPP_STAGE_GROUPS) grouped[g.key] = [];
  for (const r of rows) {
    const key = r.stage ?? "";
    if (key === "supps_waiting_on_claims") {
      grouped["supps_waiting_on_final_invoice"].push(r);
    } else if (grouped[key] !== undefined) {
      grouped[key].push(r);
    }
  }

  // Completed jobs are reference/edit, not a work queue — show most-recently
  // closed first (by when they entered the finance flow).
  if (grouped["closed_won"]) {
    grouped["closed_won"].sort((a, b) =>
      String(b.commission_ready_date ?? "").localeCompare(String(a.commission_ready_date ?? "")),
    );
  }

  // Needs-reply cards first within every group (a stable sort keeps the existing
  // order among the rest); the "Needs reply" chip narrows each group to just them.
  const needsReply = (id: string) => !!commsMap?.[id]?.needsReply;
  const needsReplyCount = commsMap ? rows.filter((r) => needsReply(r.id)).length : 0;
  for (const key of Object.keys(grouped)) {
    const list = commsFilter === "needs_reply" ? grouped[key].filter((r) => needsReply(r.id)) : grouped[key];
    grouped[key] = [...list].sort((a, b) => Number(needsReply(b.id)) - Number(needsReply(a.id)));
  }

  // Suppress the legacy alias group from the UI — its rows fold into the new bucket above
  const allFilterableGroups = SUPP_STAGE_GROUPS.filter((g) => g.key !== "supps_waiting_on_claims");
  // When a stage filter is active, render just that group; otherwise render all six
  // (minus the groups with nothing to answer while the Needs-reply chip is on).
  const visibleGroups = (
    stageFilter === "all"
      ? allFilterableGroups
      : allFilterableGroups.filter((g) => g.key === stageFilter)
  ).filter((g) => commsFilter !== "needs_reply" || stageFilter !== "all" || (grouped[g.key]?.length ?? 0) > 0);
  const stageLabel =
    stageFilter === "all" ? null : allFilterableGroups.find((g) => g.key === stageFilter)?.label;
  const activeLabel =
    [stageLabel, commsFilter === "needs_reply" ? "Needs reply" : null].filter(Boolean).join(" · ") || null;
  // The deal whose thread is open in the drawer (closes itself if the deal leaves the board).
  const openRow = openCommsId ? rows.find((r) => r.id === openCommsId) ?? null : null;
  const openGroup = openRow
    ? SUPP_STAGE_GROUPS.find(
        (g) => g.key === (openRow.stage === "supps_waiting_on_claims" ? "supps_waiting_on_final_invoice" : openRow.stage),
      )
    : undefined;

  return (
    <>
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Open supp deals" value={fmtInt(inFlight.length)} sub="ready / in flight" />
        <Kpi label="Collecting + delinquent" value={fmtInt(collecting.length)} sub={`${delinquent.length} delinquent`} valueClass={delinquent.length > 0 ? "text-red-600" : undefined} />
        <Kpi label="Total approved $" value={fmtUSD(totalApproved, { short: true })} sub="lifetime" />
        <Kpi label="Total tracked" value={fmtInt(rows.length)} />
      </div>

      {/* Lifecycle filter bar — tap a stage pill to focus on just that bucket */}
      <Panel>
        <div className="flex items-center justify-between mb-2">
          <span className="text-[11px] font-bold uppercase tracking-wide text-slate-500">
            Filter by stage — tap to focus
          </span>
          {activeLabel ? (
            <button
              onClick={() => {
                setStageFilter("all");
                setCommsFilter("all");
              }}
              className="text-[11px] px-2.5 py-1 rounded-full bg-bardin-orange/10 text-bardin-orange font-bold hover:bg-bardin-orange/20 transition-colors"
            >
              Showing: {activeLabel} · ✕ clear
            </button>
          ) : (
            <span className="text-[11px] text-slate-400">Showing all {allFilterableGroups.length} stages</span>
          )}
        </div>
        <div className="flex items-center gap-1 overflow-x-auto pb-1">
          {allFilterableGroups.map((g, i) => {
            const isActive = stageFilter === g.key;
            const isDim = stageFilter !== "all" && !isActive;
            const baseClass = "text-[11px] font-semibold whitespace-nowrap px-2.5 py-1 rounded transition-all flex-shrink-0";
            const stateClass = isActive
              ? "bg-bardin-orange text-white shadow"
              : isDim
              ? "bg-slate-100 text-slate-500 opacity-50 hover:opacity-100"
              : "bg-slate-100 text-slate-700 hover:bg-bardin-orange/10 hover:text-bardin-orange";
            return (
              <span key={g.key} className="flex items-center flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setStageFilter(isActive ? "all" : (g.key as SuppStageFilter))}
                  className={baseClass + " " + stateClass}
                >
                  {g.label}
                </button>
                {i < allFilterableGroups.length - 1 && (
                  <span className="text-slate-300 text-xs px-0.5">›</span>
                )}
              </span>
            );
          })}
          {/* Needs reply — cards whose newest text is from the homeowner and nobody has answered (Oct 16, 2026) */}
          {commsMap && (
            <>
              <span className="text-slate-300 text-xs px-1.5">|</span>
              <button
                type="button"
                onClick={() => {
                  const next: SuppCommsFilter = commsFilter === "needs_reply" ? "all" : "needs_reply";
                  setCommsFilter(next);
                  logButtonClick("supps_needs_reply_filter", { on: next === "needs_reply", count: needsReplyCount });
                }}
                className={
                  "text-[11px] font-semibold whitespace-nowrap px-2.5 py-1 rounded transition-all flex-shrink-0 " +
                  (commsFilter === "needs_reply"
                    ? "bg-red-600 text-white shadow"
                    : needsReplyCount > 0
                    ? "bg-red-50 text-red-700 border border-red-200 hover:bg-red-100"
                    : "bg-slate-100 text-slate-500 hover:bg-slate-200")
                }
                title="Only the cards whose newest text is from the homeowner and nobody has answered yet"
              >
                💬 Needs reply ({needsReplyCount})
              </button>
            </>
          )}
        </div>
      </Panel>

      {visibleGroups.map((g) => {
        const list = grouped[g.key] ?? [];
        return (
          <Panel key={g.key}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className={"inline-block w-2.5 h-2.5 rounded-full " + g.dotClass}></span>
                <h3 className="text-sm font-semibold text-bardin-ink">{g.label}</h3>
                <span className="text-xs text-slate-500">{fmtInt(list.length)}</span>
              </div>
            </div>
            {list.length === 0 ? (
              <Empty>{commsFilter === "needs_reply" ? "No texts waiting on a reply here." : "Nothing here."}</Empty>
            ) : (
              <ul className="space-y-1">
                {/* Render the full group — the supps lifecycle stages are bounded
                    work queues (collecting/delinquent are open AR), so finance
                    must see every deal. The old slice(0,20) silently truncated
                    the collecting group once it grew past 20 and hid real money
                    owed. closed_won isn't fetched into this tab, so no history
                    blow-up risk. */}
                {list.map((r) => (
                  <SupplementCard
                    key={r.id}
                    row={r}
                    stage={g.key}
                    tintClass={g.tintClass}
                    onRefresh={reload}
                    textedAt={textedAt[r.id] ?? null}
                    autoText={supplementAutoText(
                      g.key,
                      r.id,
                      r.supplement_submitted_to_insurance_date ?? null,
                      autoEnv,
                      t6Rows ? t6Rows[r.id] : null,
                    )}
                    autoRemind={autoRemind ? autoRemind[r.id] ?? null : null}
                    comms={commsMap ? commsMap[r.id] ?? null : undefined}
                    onOpenComms={(source) => {
                      setOpenCommsId(r.id);
                      logButtonClick("supps_comms_open", { stage: g.key, source, needs_reply: needsReply(r.id) });
                    }}
                    onLocalOutbound={(body) =>
                      setCommsMap((prev) => (prev ? withOptimisticOutbound(prev, r.id, body) : prev))
                    }
                  />
                ))}
              </ul>
            )}
          </Panel>
        );
      })}
      {commsFilter === "needs_reply" && visibleGroups.length === 0 && (
        <Panel>
          <Empty>No texts waiting on a reply. 🎉</Empty>
        </Panel>
      )}
    </div>

      {/* Reply drawer — one open thread at a time; the board keeps its scroll and filters underneath.
          Outside the space-y wrapper so its child margin can't push the fixed panel down. */}
      <DealCommsDrawer
        deal={
          openRow
            ? {
                id: openRow.id,
                homeowner_name: openRow.homeowner_name,
                homeowner_phone: openRow.homeowner_phone,
                homeowner_email: openRow.homeowner_email,
                claim_number: openRow.claim_number,
                stageLabel: openGroup?.label ?? openRow.stage,
                stageDotClass: openGroup?.dotClass,
                balance: openRow.post_build_balance_remaining != null ? Number(openRow.post_build_balance_remaining) : null,
              }
            : null
        }
        members={members ?? {}}
        onClose={closeComms}
        onSent={(dealId, m) => {
          setCommsMap((prev) => (prev ? withOptimisticOutbound(prev, dealId, m.body, m.created_at) : prev));
          logButtonClick("supps_comms_reply_sent", { stage: openRow?.stage ?? null });
        }}
      />
    </>
  );
}

function SupplementCard({
  row: r,
  stage,
  tintClass,
  onRefresh,
  textedAt: textedAtProp = null,
  autoText = null,
  autoRemind = null,
  comms,
  onOpenComms,
  onLocalOutbound,
}: {
  row: Pick<
    Deal,
    | "id"
    | "homeowner_name"
    | "homeowner_email"
    | "homeowner_phone"
    | "claim_number"
    | "stage"
    | "owner_id"
    | "split_rm_id"
    | "days_in_stage"
    | "stage_entered_at"
    | "phase_entered_at"
  | "phase_entered_at"
    | "created_at"
    | "chad_supplement_notes"
    | "supplement_invoice_amount"
    | "supplement_approved_amount"
    | "rcv_total"
    | "supplement_submitted_to_insurance_date"
    | "supplement_approved_date"
    | "supplement_estimate_received_date"
    | "supplement_invoice_pdf_url"
    | "supplement_invoice_generated_at"
    | "supplement_invoice_number"
    | "supplement_invoice_pdf_amount"
    | "supplement_invoice_pdf_received"
    | "supplement_invoice_pdf_pending"
    | "final_invoice_amount"
    | "initial_invoice_amount"
    | "invoice_override_amount"
    | "invoice_override_reason"
    | "final_payment_paid"
    | "post_build_balance_remaining"
    | "balance_written_off"
    | "pre_build_amount_received"
    | "post_build_amount_received"
    | "misc_credit"
    | "property_street"
    | "property_city"
    | "property_state"
    | "property_zip"
    | "insurance_company"
    | "warranty_registered_date"
    | "oc_warranty_certificate_uploaded"
  >;
  stage: string;
  tintClass: string;
  onRefresh?: () => void;
  /** when the "supps sent" text last went out to this homeowner (null = never) */
  textedAt?: string | null;
  /** what the automatic supplement text is doing on this deal (null = nothing automatic: the plain button) */
  autoText?: AutoTextState | null;
  /** Auto-remind on this deal (null = never turned on, or the board read failed) */
  autoRemind?: AutoRemindCard | null;
  /** newest text on the deal (null = none in 90 days; undefined = the board-level read failed → no strip) */
  comms?: DealCommsSummary | null;
  /** opens the reply drawer for this deal; `source` says what was tapped */
  onOpenComms?: (source: "strip" | "button") => void;
  /** a text just went out from this card (the supps-sent button), so the strip updates before the refetch */
  onLocalOutbound?: (body: string) => void;
}) {
  // Live-computed: deals.days_in_stage is frozen at 0 (never incremented).
  const days = daysInStage(r);
  const showApprovedAmt = ["collecting", "delinquent", "register_oc_warranty"].includes(stage);
  const showInvoiceAmt = ["ready_for_updated_invoice", "supps_waiting_on_final_invoice"].includes(stage);
  const balance =
    Math.max(0, (Number(r.final_invoice_amount) || 0) - (Number(r.final_payment_paid) || 0));

  // ── Missing-contact flag ────────────────────────────────────────────────
  // Mirror the exact validation supplement-invoice-send uses so the flag
  // matches what would actually block a send. Email = non-empty trimmed;
  // phone = normalizes to a valid US E.164 (10 digits, or 11 starting with 1).
  const emailMissing = !(r.homeowner_email && r.homeowner_email.trim());
  const phoneDigits = (r.homeowner_phone ?? "").replace(/\D/g, "");
  const phoneMissing = !(phoneDigits.length === 10 || (phoneDigits.length === 11 && phoneDigits[0] === "1"));
  const contactFlag = emailMissing && phoneMissing
    ? "No email or phone"
    : emailMissing
    ? "No email"
    : phoneMissing
    ? "No phone"
    : null;

  // ── "Supps Sent" inline action for ready_for_supps ──────────────────────
  const [suppsSentOpen, setSuppsSentOpen] = useState(false);
  // Today in Eastern time (a UTC date read as tomorrow after 8 PM ET).
  const [sentDate, setSentDate] = useState(() => etDate(new Date()));
  const [saving, setSaving] = useState(false);
  const { member } = useAuth();

  // ── "Text homeowner: supps sent" (Oct 12, 2026) ───────────────────────────
  // Greg texts the homeowner once the supplement request is with the insurer. The button fills the message
  // into a box; Send goes out through twilio-send-sms from the company number, tagged, so the deal's Comms
  // tab shows it and this card shows "Texted ✓ <date>" on the next load.
  const [textOpen, setTextOpen] = useState(false);
  const [textDraft, setTextDraft] = useState(SUPPLEMENT_SENT_TEXT);
  const [textSending, setTextSending] = useState(false);
  const [textErr, setTextErr] = useState<string | null>(null);
  const [textedAt, setTextedAt] = useState<string | null>(textedAtProp);
  // A newer text from the board's reads (the automatic one landing) wins over what the card had.
  useEffect(() => {
    if (!textedAtProp) return;
    setTextedAt((prev) => (prev && Date.parse(prev) > Date.parse(textedAtProp) ? prev : textedAtProp));
  }, [textedAtProp]);
  const textPhone = toE164(r.homeowner_phone);
  const showTextButton = SUPPLEMENT_TEXT_STAGES.has(stage);

  // ── The automatic version (Oct 20, 2026) ──────────────────────────────────
  // Greg: "Can we grey it and put note it's automatic in 5 minutes." While the engine has the text (about to send,
  // waiting to send, or sent), the button is greyed out so the homeowner never gets it twice. It comes back when the
  // engine could not send it, and stays as before on deals the engine never had.
  const autoLocked = autoText?.kind === "will_send" || autoText?.kind === "pending" || autoText?.kind === "sent";
  const shortDay = (iso: string) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const autoSentAt = textedAt ?? (autoText?.kind === "sent" ? autoText.at : null);
  const textLabel =
    autoText?.kind === "sent"
      ? `Texted ✓ ${autoSentAt ? shortDay(autoSentAt) + " " : ""}· automatic`
      : autoText?.kind === "pending"
      ? autoText.held ? "Text held" : autoText.sending ? "Sending text…" : "Texting automatically"
      : autoText?.kind === "will_send"
      ? "Texts automatically"
      : textedAt
      ? `Texted ✓ ${shortDay(textedAt)}`
      : "Text homeowner: supps sent";
  const autoNote =
    autoText?.kind === "will_send"
      ? "Sent automatically 5–10 minutes after you mark Supps Sent (texts go out 8 AM–7 PM)."
      : autoText?.kind === "pending"
      ? autoText.held
        ? "Held: a lot of texts went out at once, so Greg releases these."
        : autoText.sending
        ? "Going out now."
        : `Goes out automatically by ${fmtAutoEta(autoTextEta(autoText.at))}.`
      : autoText?.kind === "not_sent"
      ? `Didn't go out automatically: ${autoText.reason}. Text it yourself if it's still needed.`
      : null;

  async function handleSendSuppsText() {
    const body = textDraft.trim();
    if (!body || textSending) return;
    if (!textPhone) { setTextErr("No valid phone on this deal — add it on the deal first."); return; }
    if (!member) { setTextErr("Not signed in — refresh and try again."); return; }
    setTextSending(true);
    setTextErr(null);
    try {
      const { data, error } = await supabase.functions.invoke("twilio-send-sms", {
        body: { to: textPhone, body, deal_id: r.id, tag: SUPPLEMENT_SENT_TAG, sent_by: member.id },
      });
      if (error) throw new Error(error.message || "Send failed");
      if (data?.error) throw new Error(String(data.error));
      setTextedAt(new Date().toISOString());
      onLocalOutbound?.(body);
      setTextOpen(false);
      setTextDraft(SUPPLEMENT_SENT_TEXT);
    } catch (err: unknown) {
      setTextErr(formatSupabaseError(err));
    } finally {
      setTextSending(false);
    }
  }

  // ── "Add Supp Notes" inline action ────────────────────────────────────────
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesDraft, setNotesDraft] = useState("");
  const [notesSaving, setNotesSaving] = useState(false);

  // ── Inline upload action (ready_for_updated_invoice + supps_waiting_on_final_invoice) ──
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadInvAmt, setUploadInvAmt] = useState<string>(
    r.supplement_invoice_amount != null && Number(r.supplement_invoice_amount) > 0
      ? String(r.supplement_invoice_amount)
      : "",
  );
  const [uploadAppAmt, setUploadAppAmt] = useState<string>(
    r.supplement_approved_amount != null && Number(r.supplement_approved_amount) > 0
      ? String(r.supplement_approved_amount)
      : "",
  );
  const [uploadAppDate, setUploadAppDate] = useState<string>(
    r.supplement_approved_date
      ? String(r.supplement_approved_date).slice(0, 10)
      : new Date().toISOString().slice(0, 10),
  );
  // 2026-10-15: the day Apex's rebuilt estimate arrived. Stamped with the supplement-invoice upload (defaults to
  // today), editable on the card afterwards; Books reads it to tell Apex's drafting time from the insurer's review.
  const [uploadEstDate, setUploadEstDate] = useState<string>(
    r.supplement_estimate_received_date
      ? String(r.supplement_estimate_received_date).slice(0, 10)
      : new Date().toISOString().slice(0, 10),
  );
  const [estDateLocal, setEstDateLocal] = useState<string>(
    r.supplement_estimate_received_date ? String(r.supplement_estimate_received_date).slice(0, 10) : "",
  );
  const [estDateSaving, setEstDateSaving] = useState(false);
  const [uploadSaving, setUploadSaving] = useState(false);
  const [uploadErr, setUploadErr] = useState<string | null>(null);

  // ── "All money collected → register warranty" inline action (May 15, 2026) ──
  // collecting/delinquent → register_oc_warranty. Mirrors the TransitionButton
  // balance_zero gate: post_build_balance_remaining <= 0 AND some payment
  // actually received (post-May 7 fix — default-zero balance can't trivially
  // satisfy the gate). Keeps Shannon's flow inline so she doesn't have to nav
  // into each deal once money has fully landed.
  const [collectedOpen, setCollectedOpen] = useState(false);
  const [collectedSaving, setCollectedSaving] = useState(false);
  const [collectedErr, setCollectedErr] = useState<string | null>(null);

  // ── "Write off remaining balance" inline action (May 21, 2026) ──
  // For small uncollectable residuals (e.g. an ACV check that came in a few
  // dollars short). Shannon forgives the leftover directly — no approval — and
  // the deal advances to register_oc_warranty. The forgiven amount lands in
  // deals.balance_written_off (subtracted by recompute_deal_payment_summary),
  // so cash-received columns stay honest. Misc credit does NOT touch the AR
  // balance — that's why typing a credit there never zeroed it out.
  const [writeoffOpen, setWriteoffOpen] = useState(false);
  const [writeoffSaving, setWriteoffSaving] = useState(false);
  const [writeoffErr, setWriteoffErr] = useState<string | null>(null);
  const [writeoffReason, setWriteoffReason] = useState<string>("");

  // ── Targeted residual write-off (supps stages, Jul 1, 2026) ──
  // Clears a small first-payment/commission-gate shortfall (e.g. a short ACV
  // check) WITHOUT forgiving the full uncollected final-invoice balance and
  // WITHOUT advancing the stage. Shannon enters the exact amount.
  const [residualOpen, setResidualOpen] = useState(false);
  const [residualSaving, setResidualSaving] = useState(false);
  const [residualErr, setResidualErr] = useState<string | null>(null);
  const [residualAmount, setResidualAmount] = useState<string>("");
  const [residualReason, setResidualReason] = useState<string>("");

  // ── "Mark delinquent" manual transition (May 26, 2026) ──
  // collecting → delinquent. Mirrors the auto_promote_to_delinquent cron
  // (file 03 of the finance redesign) but lets Shannon flag a deal early
  // without waiting for the 30-day-since-final-invoice cron run. Same side
  // effects as the cron: finance_audit_log row + notification to Shannon,
  // tagged source 'manual'.
  const [markDelinqOpen, setMarkDelinqOpen] = useState(false);
  const [markDelinqSaving, setMarkDelinqSaving] = useState(false);
  const [markDelinqErr, setMarkDelinqErr] = useState<string | null>(null);
  const [markDelinqReason, setMarkDelinqReason] = useState<string>("");

  // ── Invoice override release (Invoice Derivation Phase 1, Jul 14, 2026) ──
  // When invoice_override_amount is set, the DB derivation trigger uses it
  // instead of the computed net invoice (gross − non-collect). The chip below
  // surfaces the override + reason; releasing clears it and the trigger
  // recomputes final_invoice_amount + balance in the same statement.
  const [overrideReleasing, setOverrideReleasing] = useState(false);
  async function handleReleaseOverride() {
    if (!window.confirm(
      "Release this deal to automatic invoice calculation?\n\nThe final invoice will recompute from the scope (net of non-collect) and the balance will update immediately.",
    )) return;
    setOverrideReleasing(true);
    const before = Number(r.invoice_override_amount) || 0;
    const { error } = await supabase
      .from("deals")
      .update({
        invoice_override_amount: null,
        invoice_override_reason: null,
        invoice_override_by: null,
        invoice_override_at: null,
      })
      .eq("id", r.id);
    if (error) {
      window.alert("Release failed: " + error.message);
    } else {
      await supabase.from("finance_audit_log").insert({
        deal_id: r.id,
        actor_id: member?.id ?? null,
        action: "final_invoice_amount_set",
        before_jsonb: { invoice_override_amount: before },
        after_jsonb: { invoice_override_amount: null, source: "override_released" },
        reason: "Invoice override released — deal returned to automatic derivation.",
        is_financial: true,
      }).then(() => {}, () => {});
      onRefresh?.();
    }
    setOverrideReleasing(false);
  }

  // ── Misc Credit inline editor (May 15, 2026) ──
  // Misc Credit moved from the RM-facing Scope editor to Shannon's Finance
  // area per the Non-Collect Revenue redesign. Shannon enters goodwill /
  // referral / deductible-assist credit post-build, before commissions are
  // pushed to Chad. Renders on every supplements-lifecycle stage so she can
  // set or correct it at any point.
  const [miscOpen, setMiscOpen] = useState(false);
  const [miscDraft, setMiscDraft] = useState<string>(
    r.misc_credit != null && Number(r.misc_credit) > 0 ? String(r.misc_credit) : "",
  );
  const [miscSaving, setMiscSaving] = useState(false);
  const [miscErr, setMiscErr] = useState<string | null>(null);

  async function handleSaveMiscCredit() {
    setMiscSaving(true);
    setMiscErr(null);
    try {
      const amount = miscDraft.trim() === "" ? 0 : Number(miscDraft);
      if (!Number.isFinite(amount) || amount < 0) {
        throw new Error("Enter a valid amount ($0 or greater).");
      }
      const { error } = await supabase
        .from("deals")
        .update({ misc_credit: amount })
        .eq("id", r.id);
      if (error) throw error;
      // Audit row so the change is traceable in finance_audit_log (best-effort)
      try {
        await supabase.from("finance_audit_log").insert({
          deal_id: r.id,
          actor_id: member?.id ?? null,
          action: "misc_credit_set",
          is_financial: true,
          after_jsonb: { misc_credit: amount, source: "finance_supplements_tab" },
        });
      } catch { /* audit row failure shouldn't block the value save */ }
      setMiscOpen(false);
      onRefresh?.();
    } catch (err) {
      setMiscErr(formatSupabaseError(err));
    } finally {
      setMiscSaving(false);
    }
  }
  const balanceN = Number((r.post_build_balance_remaining ?? 0) as number | string);
  const totalReceivedN =
    Number((r.pre_build_amount_received ?? 0) as number | string) +
    Number((r.post_build_amount_received ?? 0) as number | string) +
    Number((r.final_payment_paid ?? 0) as number | string);
  const balanceZeroMet = balanceN <= 0 && totalReceivedN > 0;

  async function handleAllMoneyCollected() {
    if (!balanceZeroMet) return;
    setCollectedSaving(true);
    setCollectedErr(null);
    try {
      const fromStage = r.stage ?? stage;
      const { error } = await supabase
        .from("deals")
        .update({
          stage: "register_oc_warranty",
          days_in_stage: 0,
        })
        .eq("id", r.id);
      if (error) throw error;
      await supabase.from("activity_log").insert({
        deal_id: r.id,
        activity_type: "stage_change",
        title: "All money collected",
        description: `Advanced from ${fromStage} to register_oc_warranty (balance $0, total received $${totalReceivedN.toFixed(2)})`,
        metadata: {
          from_stage: fromStage,
          to_stage: "register_oc_warranty",
          balance_remaining: balanceN,
          total_received: totalReceivedN,
        },
      });
      setCollectedOpen(false);
      onRefresh?.();
    } catch (err: unknown) {
      setCollectedErr(formatSupabaseError(err));
    } finally {
      setCollectedSaving(false);
    }
  }

  async function handleWriteOffRemaining() {
    if (balanceN <= 0) return;
    setWriteoffSaving(true);
    setWriteoffErr(null);
    try {
      const fromStage = r.stage ?? stage;
      const priorWriteoff = Number((r.balance_written_off ?? 0) as number | string);
      const newWriteoff = Math.round((priorWriteoff + balanceN) * 100) / 100;
      // Set the write-off AND advance in one update. The recompute trigger
      // fires on balance_written_off change and zeroes the balance; stage is
      // already register_oc_warranty (non-terminal) so recompute runs.
      const { error } = await supabase
        .from("deals")
        .update({
          balance_written_off: newWriteoff,
          stage: "register_oc_warranty",
          days_in_stage: 0,
        })
        .eq("id", r.id);
      if (error) throw error;
      // Financial audit row — who forgave what, and why.
      try {
        await supabase.from("finance_audit_log").insert({
          deal_id: r.id,
          actor_id: member?.id ?? null,
          action: "balance_written_off",
          is_financial: true,
          amount_delta: -balanceN,
          after_jsonb: {
            written_off: balanceN,
            total_written_off: newWriteoff,
            final_invoice_amount: Number((r.final_invoice_amount ?? 0) as number | string),
            reason: writeoffReason.trim() || null,
            source: "finance_supplements_tab",
          },
          reason: `Wrote off $${balanceN.toFixed(2)} residual balance${writeoffReason.trim() ? `: "${writeoffReason.trim().slice(0, 200)}"` : ""}`,
        });
      } catch { /* audit failure shouldn't block the write-off */ }
      // Stage-change activity log (mirrors handleAllMoneyCollected).
      await supabase.from("activity_log").insert({
        deal_id: r.id,
        activity_type: "stage_change",
        title: "Balance written off",
        description: `Advanced from ${fromStage} to register_oc_warranty (wrote off $${balanceN.toFixed(2)} residual)`,
        metadata: {
          from_stage: fromStage,
          to_stage: "register_oc_warranty",
          written_off: balanceN,
          by: member?.full_name ?? null,
        },
      });
      setWriteoffOpen(false);
      onRefresh?.();
    } catch (err: unknown) {
      setWriteoffErr(formatSupabaseError(err));
    } finally {
      setWriteoffSaving(false);
    }
  }

  // Targeted residual write-off for the supps stages (Jul 1, 2026). Forgives a
  // Shannon-entered amount (not the full balance) and does NOT advance the
  // stage — the deal keeps moving through the supps lifecycle normally. Purpose:
  // clear a small first-payment shortfall so the commission gate can open (the
  // gate credits balance_written_off toward firstPaymentToBardin). The write-off
  // reduces AR by the amount via the recompute trigger; cash-received columns
  // stay honest.
  async function handleWriteOffResidual() {
    const amt = Math.round(Number(residualAmount) * 100) / 100;
    if (!Number.isFinite(amt) || amt <= 0) {
      setResidualErr("Enter an amount greater than $0.");
      return;
    }
    setResidualSaving(true);
    setResidualErr(null);
    try {
      const priorWriteoff = Number((r.balance_written_off ?? 0) as number | string);
      const newWriteoff = Math.round((priorWriteoff + amt) * 100) / 100;
      const { error } = await supabase
        .from("deals")
        .update({ balance_written_off: newWriteoff })
        .eq("id", r.id);
      if (error) throw error;
      try {
        await supabase.from("finance_audit_log").insert({
          deal_id: r.id,
          actor_id: member?.id ?? null,
          action: "balance_written_off",
          is_financial: true,
          amount_delta: -amt,
          after_jsonb: {
            written_off: amt,
            total_written_off: newWriteoff,
            final_invoice_amount: Number((r.final_invoice_amount ?? 0) as number | string),
            reason: residualReason.trim() || null,
            source: "finance_supps_residual",
            stage_at_writeoff: r.stage ?? stage,
          },
          reason: `Wrote off $${amt.toFixed(2)} residual to clear the commission gate${residualReason.trim() ? `: "${residualReason.trim().slice(0, 200)}"` : ""}`,
        });
      } catch { /* audit failure shouldn't block the write-off */ }
      setResidualOpen(false);
      onRefresh?.();
    } catch (err: unknown) {
      setResidualErr(formatSupabaseError(err));
    } finally {
      setResidualSaving(false);
    }
  }

  // Manual collecting → delinquent. Mirrors auto_promote_to_delinquent (cron,
  // finance redesign file 03): flip stage, write a finance_audit_log row, and
  // notify Shannon — tagged source 'manual' so it's distinguishable from the
  // cron-driven promotions. Notification skipped when the actor IS Shannon.
  async function handleMarkDelinquent() {
    setMarkDelinqSaving(true);
    setMarkDelinqErr(null);
    try {
      const fromStage = r.stage ?? stage;
      const reasonText = markDelinqReason.trim();
      const { error } = await supabase
        .from("deals")
        .update({ stage: "delinquent", days_in_stage: 0 })
        .eq("id", r.id);
      if (error) throw error;

      // Financial audit row — mirrors the cron's action name + payload shape.
      try {
        await supabase.from("finance_audit_log").insert({
          deal_id: r.id,
          actor_id: member?.id ?? null,
          action: "manual_promote_to_delinquent",
          is_financial: true,
          before_jsonb: { stage: "collecting" },
          after_jsonb: {
            stage: "delinquent",
            balance,
            days_in_stage: days,
            source: "manual",
            reason: reasonText || null,
          },
          reason:
            `Manually marked delinquent at ${days} day${days === 1 ? "" : "s"} in collecting · ` +
            `balance $${balance.toFixed(2)}` +
            (reasonText ? ` · "${reasonText.slice(0, 200)}"` : ""),
        });
      } catch { /* audit failure shouldn't block the transition */ }

      // Notify Shannon (mirrors the cron) — skip if the actor is Shannon herself.
      try {
        const { data: shannon } = await supabase
          .from("team_roster")
          .select("id")
          .eq("email", "sser@bardinroofing.com")
          .eq("is_active", true)
          .maybeSingle();
        if (shannon?.id && shannon.id !== member?.id) {
          await supabase.from("notifications").insert({
            user_id: shannon.id,
            type: "deal_delinquent",
            title: `Deal marked delinquent — ${r.homeowner_name ?? "(no name)"}`,
            body:
              `Manually flagged by ${member?.full_name ?? "finance"} · ` +
              `$${balance.toFixed(2)} outstanding`,
            deal_id: r.id,
            metadata: {
              balance,
              days_in_stage: days,
              source: "manual",
              url: "/finance?view=shannon&tab=supplements",
            },
          });
        }
      } catch { /* notification failure shouldn't block the transition */ }

      // Stage-change activity log (mirrors handleAllMoneyCollected / write-off).
      await supabase.from("activity_log").insert({
        deal_id: r.id,
        activity_type: "stage_change",
        title: "Marked delinquent",
        description:
          `Advanced from ${fromStage} to delinquent (manual)` +
          (reasonText ? ` — ${reasonText.slice(0, 200)}` : ""),
        metadata: {
          from_stage: fromStage,
          to_stage: "delinquent",
          balance,
          source: "manual",
          by: member?.full_name ?? null,
        },
      });

      setMarkDelinqOpen(false);
      onRefresh?.();
    } catch (err: unknown) {
      setMarkDelinqErr(formatSupabaseError(err));
    } finally {
      setMarkDelinqSaving(false);
    }
  }

  // ── "Mark Closed Won" inline action (register_oc_warranty → closed_won) ──
  // The warranty is registered elsewhere (deal detail transition / Jennifer's
  // QC board), which stamps warranty_registered_date + the OC certificate. Once
  // present, Shannon closes the deal from her card here without having to nav
  // into the deal. Gate mirrors the deal-detail register_oc_warranty transition.
  // (May 21, 2026: registration date dropped per Greg — OC certificate only.)
  const ocCertUploaded = r.oc_warranty_certificate_uploaded === true;
  const closedWonReady = ocCertUploaded;
  const [closedWonOpen, setClosedWonOpen] = useState(false);
  const [closedWonSaving, setClosedWonSaving] = useState(false);
  const [closedWonErr, setClosedWonErr] = useState<string | null>(null);

  // ── "Reopen to Collecting" inline action (closed_won → collecting) ──────────
  // Jun 22, 2026: a completed job needs to be re-invoiced (e.g. a payment that
  // didn't clear). Moving out of the terminal stage lets recompute run; we call
  // it explicitly so the frozen balance refreshes from current deal_payments.
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenSaving, setReopenSaving] = useState(false);
  const [reopenErr, setReopenErr] = useState<string | null>(null);

  async function handleReopenToCollecting() {
    setReopenSaving(true);
    setReopenErr(null);
    try {
      const fromStage = r.stage ?? stage;
      const { error } = await supabase
        .from("deals")
        .update({ stage: "collecting", days_in_stage: 0 })
        .eq("id", r.id);
      if (error) throw error;
      // Unfreeze the balance now that it's out of a terminal stage. recompute
      // early-returns on closed_won, so the cached balance is stale until this runs.
      await supabase.rpc("recompute_deal_payment_summary", { p_deal_id: r.id });
      await supabase.from("activity_log").insert({
        deal_id: r.id,
        activity_type: "stage_change",
        title: "Reopened to Collecting",
        description: `Reopened from ${fromStage} to collecting to re-invoice the balance due`,
        metadata: {
          from_stage: fromStage,
          to_stage: "collecting",
          by: member?.full_name ?? null,
          source: "finance_reopen",
        },
      });
      setReopenOpen(false);
      onRefresh?.();
    } catch (err: unknown) {
      setReopenErr(formatSupabaseError(err));
    } finally {
      setReopenSaving(false);
    }
  }

  async function handleMarkClosedWon() {
    if (!closedWonReady) return;
    setClosedWonSaving(true);
    setClosedWonErr(null);
    try {
      const fromStage = r.stage ?? stage;
      const { error } = await supabase
        .from("deals")
        .update({ stage: "closed_won", days_in_stage: 0 })
        .eq("id", r.id);
      if (error) throw error;
      await supabase.from("activity_log").insert({
        deal_id: r.id,
        activity_type: "stage_change",
        title: "Marked Closed Won",
        description: `Advanced from ${fromStage} to closed_won (OC warranty certificate on file)`,
        metadata: {
          from_stage: fromStage,
          to_stage: "closed_won",
          warranty_registered_date: r.warranty_registered_date ?? null,
          by: member?.full_name ?? null,
        },
      });
      setClosedWonOpen(false);
      onRefresh?.();
    } catch (err: unknown) {
      setClosedWonErr(formatSupabaseError(err));
    } finally {
      setClosedWonSaving(false);
    }
  }

  async function handleSaveNotes() {
    setNotesSaving(true);
    try {
      const { error } = await supabase
        .from("deals")
        .update({ chad_supplement_notes: notesDraft || null })
        .eq("id", r.id);
      if (error) throw error;
      setNotesOpen(false);
      onRefresh?.();
    } catch (err: unknown) {
      alert("Save failed: " + formatSupabaseError(err));
    } finally {
      setNotesSaving(false);
    }
  }

  async function handleEstimateDateChange(next: string) {
    const before = r.supplement_estimate_received_date ? String(r.supplement_estimate_received_date).slice(0, 10) : null;
    const after = next || null;
    setEstDateLocal(next);
    if (after === before) return;
    setEstDateSaving(true);
    try {
      const { error } = await supabase
        .from("deals")
        .update({ supplement_estimate_received_date: after })
        .eq("id", r.id);
      if (error) throw error;
      await supabase.from("finance_audit_log").insert({
        deal_id: r.id,
        actor_id: member?.id ?? null,
        action: "supplement_estimate_received_date_set",
        before_jsonb: { supplement_estimate_received_date: before },
        after_jsonb: { supplement_estimate_received_date: after },
        reason: "Apex estimate date edited on the Supplements card",
        is_financial: false,
      }).then(() => {}, () => {});
      onRefresh?.();
    } catch (err: unknown) {
      alert("Save failed: " + formatSupabaseError(err));
    } finally {
      setEstDateSaving(false);
    }
  }

  const chadNotesPresent = !!(r.chad_supplement_notes && r.chad_supplement_notes.trim().length > 0);

  async function handleSuppsSent() {
    if (!sentDate) { alert("Please enter the date supplements were sent."); return; }
    setSaving(true);
    try {
      const { error } = await supabase
        .from("deals")
        .update({
          stage: "ready_for_updated_invoice",
          supplement_submitted_to_insurance_date: sentDate,
          days_in_stage: 0,
        })
        .eq("id", r.id);
      if (error) throw error;
      await supabase.from("activity_log").insert({
        deal_id: r.id,
        activity_type: "stage_change",
        title: "Sent supplements to Apex",
        description: `Advanced from ready_for_supps to ready_for_updated_invoice (sent ${sentDate})`,
        metadata: { from_stage: "ready_for_supps", to_stage: "ready_for_updated_invoice" },
      });
      setSuppsSentOpen(false);
      onRefresh?.();
    } catch (err: unknown) {
      alert("Save failed: " + formatSupabaseError(err));
    } finally {
      setSaving(false);
    }
  }

  // Inline upload + advance for the two doc-upload stages on the card itself.
  // Mirrors TransitionButton's behavior (boolean flag + amount + advance), and adds
  // the real storage upload + deal_documents insert that DealDetail's docs tab does.
  async function handleUploadAndAdvance() {
    setUploadErr(null);
    const isInvoiceStage = stage === "ready_for_updated_invoice";
    const isFinalStage =
      stage === "supps_waiting_on_final_invoice" || stage === "supps_waiting_on_claims";
    if (!isInvoiceStage && !isFinalStage) return;

    // Validate required fields per stage.
    if (!uploadFile) { setUploadErr("Pick a file to upload."); return; }
    if (isInvoiceStage) {
      if (!uploadInvAmt || Number(uploadInvAmt) <= 0) {
        setUploadErr("Enter the supplement amount requested.");
        return;
      }
    } else {
      if (!uploadAppAmt || Number(uploadAppAmt) <= 0) {
        setUploadErr("Enter the approved supplement amount.");
        return;
      }
      if (!uploadAppDate) {
        setUploadErr("Enter the insurance approval date.");
        return;
      }
    }
    if (!member) { setUploadErr("Not signed in — refresh and try again."); return; }

    setUploadSaving(true);
    try {
      // Convert iPhone HEIC/HEIF to JPEG so the uploaded paperwork renders in
      // every browser. No-op for PDFs and standard image types.
      const upFile = await heicToJpeg(uploadFile);

      // Refresh session to avoid mobile expired-token upload failures (mirrors DealDetail).
      await supabase.auth.refreshSession();

      const safeName = upFile.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const path = `${r.id}/${Date.now()}-${safeName}`;
      const docType = isInvoiceStage ? "supplement_invoice" : "finalized_insurance_paperwork";

      // Upload to deal-documents with one retry — same pattern as DealDetail.handleUpload.
      let uploadErrMsg: string | null = null;
      for (let attempt = 0; attempt < 2; attempt++) {
        const { error: upErr } = await supabase.storage
          .from("deal-documents")
          .upload(path, upFile, {
            contentType: upFile.type || "application/octet-stream",
          });
        if (!upErr) { uploadErrMsg = null; break; }
        uploadErrMsg = upErr.message;
        if (attempt === 0) await new Promise((res) => setTimeout(res, 1000));
      }
      if (uploadErrMsg) throw new Error(uploadErrMsg);

      const { error: docErr } = await supabase.from("deal_documents").insert({
        deal_id: r.id,
        file_name: upFile.name,
        file_type: docType,
        storage_path: path,
        file_size: upFile.size,
        mime_type: upFile.type,
        uploaded_by: member.id,
      });
      if (docErr) throw docErr;

      // Build the deal update payload per stage.
      const update: Record<string, unknown> = { days_in_stage: 0 };
      if (isInvoiceStage) {
        update.supplement_invoice_uploaded = true;
        update.supplement_invoice_amount = Number(uploadInvAmt);
        update.supplement_estimate_received_date = uploadEstDate || null;
        update.stage = "supps_waiting_on_final_invoice";
      } else {
        update.finalized_insurance_uploaded = true;
        update.supplement_approved_amount = Number(uploadAppAmt);
        update.supplement_approved_date = uploadAppDate;
        update.stage = "collecting";
        // Jul 14, 2026 (Invoice Derivation Phase 1): the final invoice is now
        // DERIVED by the deals_recompute_final_invoice DB trigger — writing
        // supplement_approved_amount + stage above is enough; the trigger
        // recomputes final_invoice_amount (net of non-collect) and the balance
        // in the same UPDATE. The old client-side calcScope roll-in that lived
        // here was removed: it stamped the GROSS number and only covered this
        // one entry path (the deal-page transition button was a second door
        // that skipped it — the Naylor/Poma bug).
      }

      const { error: dealErr } = await supabase.from("deals").update(update).eq("id", r.id);
      if (dealErr) throw dealErr;

      const fromStage = r.stage ?? stage;
      const toStage = update.stage as string;
      await supabase.from("activity_log").insert({
        deal_id: r.id,
        activity_type: "stage_change",
        title: isInvoiceStage
          ? "Uploaded supplement invoice"
          : "Uploaded finalized insurance paperwork",
        description: isInvoiceStage
          ? `Advanced from ${fromStage} to supps_waiting_on_final_invoice (invoice $${Number(
              uploadInvAmt,
            ).toFixed(2)})`
          : `Advanced from ${fromStage} to collecting (approved $${Number(uploadAppAmt).toFixed(
              2,
            )} on ${uploadAppDate})`,
        metadata: {
          from_stage: fromStage,
          to_stage: toStage,
          file_name: uploadFile.name,
          file_type: docType,
        },
      });

      setUploadOpen(false);
      setUploadFile(null);
      onRefresh?.();
    } catch (err: unknown) {
      setUploadErr(formatSupabaseError(err));
    } finally {
      setUploadSaving(false);
    }
  }

  return (
    <li className={"rounded-lg border border-slate-200 p-3 " + tintClass}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-medium text-sm text-bardin-ink">
            <DealLink id={r.id}>{r.homeowner_name ?? "(no name)"}</DealLink>
          </div>
          <div className="text-xs text-slate-500">
            {r.claim_number ? `#${r.claim_number}` : ""}
          </div>
          <DealAgeChips
            deal={r}
            warnAt={30}
            dangerAt={stage === "delinquent" ? 0 : 45}
            className="mt-0.5"
          />
          {contactFlag && (
            <div
              className={
                "mt-1 inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded " +
                (emailMissing && phoneMissing
                  ? "bg-red-100 text-red-700"
                  : "bg-amber-100 text-amber-700")
              }
              title="Invoice send needs this on file — email for the Email/Both options, a valid phone for Text/Both."
            >
              ⚠ {contactFlag}
            </div>
          )}
          {autoRemind && REMIND_STAGES.has(stage) && (
            <div>
              <AutoRemindChip settings={autoRemind.settings} anchorSentAt={autoRemind.anchorSentAt} rows={autoRemind.rows} />
            </div>
          )}
          {r.chad_supplement_notes && (
            <div className="mt-1.5 text-xs text-slate-700 bg-white rounded border-l-2 border-bardin-orange pl-2 py-1">
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">Chad's notes:</span>{" "}
              {r.chad_supplement_notes.length > 110
                ? r.chad_supplement_notes.slice(0, 110) + "…"
                : r.chad_supplement_notes}
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          {(showInvoiceAmt || showApprovedAmt) && (
            <label
              className="text-[11px] text-slate-500 flex items-center gap-1"
              title="The day Apex's rebuilt estimate arrived — edit if it is wrong"
            >
              Apex est.
              <input
                type="date"
                value={estDateLocal}
                disabled={estDateSaving}
                onChange={(e) => void handleEstimateDateChange(e.target.value)}
                className="text-[11px] border border-slate-200 rounded px-1 py-0.5 bg-white disabled:opacity-50"
              />
            </label>
          )}
          {showInvoiceAmt && r.supplement_invoice_amount != null && (
            <div className="text-xs text-slate-600">
              Requested: <strong>{fmtUSD(Number(r.supplement_invoice_amount) || 0, { short: true })}</strong>
            </div>
          )}
          {showApprovedAmt && r.supplement_approved_amount != null && (
            <div className="text-xs text-emerald-700">
              Approved: <strong>{fmtUSD(Number(r.supplement_approved_amount) || 0, { short: true })}</strong>
            </div>
          )}
          {stage === "delinquent" && balance > 0 && (
            <div className="text-xs text-red-700 font-semibold">
              Owed: {fmtUSD(balance, { short: true })}
            </div>
          )}
        </div>
      </div>

      {/* ── Newest text + reply drawer (Oct 16, 2026): read and answer without opening the deal ── */}
      {comms !== undefined && onOpenComms && (
        <CommsStrip summary={comms} onOpen={() => onOpenComms("strip")} className="mt-2" />
      )}

      {/* ── Invoice override chip (derivation paused for this deal) ── */}
      {r.invoice_override_amount != null && (
        <div className="mt-2 rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-2 text-xs text-amber-900">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span>
              🔒 <strong>Invoice override {fmtUSD(Number(r.invoice_override_amount) || 0)}</strong>
              <span className="text-amber-700"> — auto-calculation paused</span>
            </span>
            <button
              onClick={handleReleaseOverride}
              disabled={overrideReleasing}
              className="text-[11px] font-semibold px-2.5 py-1 rounded-lg border border-amber-400 text-amber-800 bg-white hover:bg-amber-100 transition disabled:opacity-40"
            >
              {overrideReleasing ? "Releasing…" : "Release to auto-calc"}
            </button>
          </div>
          {r.invoice_override_reason && (
            <div className="mt-1 text-[11px] leading-snug text-amber-800">{r.invoice_override_reason}</div>
          )}
        </div>
      )}

      {/* ── Supplement notes inline editor ── */}
      {notesOpen && (
        <div className="mt-2.5 pt-2.5 border-t border-slate-200/60 space-y-2">
          <div className="text-[11px] font-semibold text-slate-700 uppercase tracking-wide">Supplement Notes</div>
          <textarea
            rows={4}
            value={notesDraft}
            onChange={(e) => setNotesDraft(e.target.value)}
            placeholder="Enter supplement notes — Apex contact, scope details, outstanding items…"
            className="w-full border border-slate-300 rounded-lg px-2.5 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-bardin-orange/40 resize-y"
            autoFocus
          />
          <div className="flex gap-2">
            <button
              onClick={handleSaveNotes}
              disabled={notesSaving}
              className="flex-1 text-[12px] font-semibold px-3 py-1.5 rounded-lg bg-bardin-orange text-white hover:bg-bardin-orange/90 transition disabled:opacity-40"
            >
              {notesSaving ? "Saving…" : "💾 Save Notes"}
            </button>
            <button
              onClick={() => setNotesOpen(false)}
              className="text-[12px] px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── Add Supp Notes button + Reconcile + Misc Credit (when editor closed) ── */}
      {!notesOpen && !suppsSentOpen && !miscOpen && !reopenOpen && !residualOpen && (
        <div className="mt-2 pt-2 border-t border-slate-200/60 flex items-center gap-2 flex-wrap">
          {onOpenComms && (
            <button
              onClick={() => onOpenComms("button")}
              className={
                "text-[11px] font-semibold px-2.5 py-1.5 rounded-lg border transition " +
                (comms?.needsReply
                  ? "bg-red-50 text-red-700 border-red-200 hover:bg-red-100"
                  : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50")
              }
              title="Open the text thread — read the homeowner's texts and reply from the board"
            >
              💬 {comms?.needsReply ? "Reply" : "Texts"}
            </button>
          )}
          <button
            onClick={() => {
              setNotesDraft(r.chad_supplement_notes ?? "");
              setNotesOpen(true);
              setSuppsSentOpen(false);
            }}
            className={
              "text-[11px] font-semibold px-2.5 py-1.5 rounded-lg border transition " +
              (r.chad_supplement_notes
                ? "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100"
                : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50")
            }
          >
            📝 {r.chad_supplement_notes ? "Supp Notes" : "Add Supp Notes"}
          </button>
          <ReconcileButton dealId={r.id} size="xs" />
          <button
            onClick={() => {
              setMiscDraft(r.misc_credit != null && Number(r.misc_credit) > 0 ? String(r.misc_credit) : "");
              setMiscErr(null);
              setMiscOpen(true);
            }}
            className={
              "text-[11px] font-semibold px-2.5 py-1.5 rounded-lg border transition " +
              (r.misc_credit != null && Number(r.misc_credit) > 0
                ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50")
            }
            title="Goodwill / referral / deductible assist. Subtracted from commissionable revenue."
          >
            💵 {r.misc_credit != null && Number(r.misc_credit) > 0
              ? `Misc Credit ${fmtUSD(Number(r.misc_credit), { short: true })}`
              : "Set Misc Credit"}
          </button>
          {showTextButton && (
            <button
              onClick={() => { setTextDraft(SUPPLEMENT_SENT_TEXT); setTextErr(null); setTextOpen(true); }}
              disabled={textOpen || autoLocked}
              className={
                "text-[11px] font-semibold px-2.5 py-1.5 rounded-lg border transition " +
                (autoLocked
                  ? "bg-slate-100 border-slate-200 cursor-not-allowed " +
                    (autoText?.kind === "sent" ? "text-emerald-700" : "text-slate-500")
                  : "disabled:opacity-40 " +
                    (textedAt
                      ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                      : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"))
              }
              title={
                autoLocked
                  ? "This text goes out by itself from the company number 5–10 minutes after Supps Sent (texts go out 8 AM–7 PM), with the email when there is one. It shows on the deal's Comms tab, and here as Texted ✓."
                  : textPhone
                  ? `Fills in the "completion package and supplement request sent" message; you read it and press Send. Goes to ${r.homeowner_phone} from the company number and shows on the deal's Comms tab.`
                  : "No valid phone on this deal — add it on the deal first."
              }
            >
              💬 {textLabel}
            </button>
          )}
          {showTextButton && autoNote && (
            <div className="basis-full text-[11px] leading-snug text-slate-500">{autoNote}</div>
          )}
        </div>
      )}

      {/* ── "Supps sent" text to the homeowner: prefilled, read, Send (Oct 12, 2026) ── */}
      {textOpen && !autoLocked && (
        <div className="mt-2.5 pt-2.5 border-t border-slate-200/60 space-y-2">
          <div className="text-[11px] font-semibold text-slate-700 uppercase tracking-wide">
            Text to {r.homeowner_name ?? "the homeowner"}{textPhone ? ` · ${r.homeowner_phone}` : ""}
          </div>
          {textedAt && (
            <div className="text-[11px] text-emerald-700">Already texted on {new Date(textedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} — sending again sends a second text.</div>
          )}
          <textarea
            rows={9}
            value={textDraft}
            onChange={(e) => setTextDraft(e.target.value)}
            className="w-full border border-slate-300 rounded-lg px-2.5 py-2 text-xs leading-snug focus:outline-none focus:ring-2 focus:ring-bardin-teal/40 resize-y"
            disabled={textSending}
            autoFocus
          />
          {textErr && <div className="text-[11px] text-red-700">⚠ {textErr}</div>}
          <div className="flex gap-2">
            <button
              onClick={handleSendSuppsText}
              disabled={textSending || !textDraft.trim() || !textPhone}
              className="flex-1 text-[12px] font-semibold px-3 py-1.5 rounded-lg bg-bardin-teal text-white hover:bg-bardin-teal/90 transition disabled:opacity-40"
            >
              {textSending ? "Sending…" : "💬 Send text"}
            </button>
            <button
              onClick={() => { setTextOpen(false); setTextErr(null); }}
              disabled={textSending}
              className="text-[12px] px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition disabled:opacity-40"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── Misc Credit inline editor (Shannon owns; was on RM scope editor pre-May 15, 2026) ── */}
      {miscOpen && (
        <div className="mt-2.5 pt-2.5 border-t border-slate-200/60 space-y-2">
          <div className="text-[11px] font-semibold text-slate-700 uppercase tracking-wide">
            Misc Credit
          </div>
          <div className="text-[11px] text-slate-500">
            Goodwill, referral discount, or deductible-assist amount applied toward the homeowner's deductible.
            Subtracted from commissionable revenue (no longer triggers the ≥$501 split — voided May 15, 2026).
          </div>
          <div className="relative">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span>
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              min={0}
              value={miscDraft}
              onChange={(e) => setMiscDraft(e.target.value)}
              placeholder="0.00"
              className="w-full pl-7 pr-2 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-bardin-orange/40"
              autoFocus
              disabled={miscSaving}
            />
          </div>
          {miscErr && <div className="text-[11px] text-red-700">⚠ {miscErr}</div>}
          <div className="flex gap-2">
            <button
              onClick={handleSaveMiscCredit}
              disabled={miscSaving}
              className="flex-1 text-[12px] font-semibold px-3 py-1.5 rounded-lg bg-bardin-orange text-white hover:bg-bardin-orange/90 transition disabled:opacity-40"
            >
              {miscSaving ? "Saving…" : "💾 Save Misc Credit"}
            </button>
            <button
              onClick={() => { setMiscOpen(false); setMiscErr(null); }}
              disabled={miscSaving}
              className="text-[12px] px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition disabled:opacity-40"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── Action buttons ── */}
      {stage === "ready_for_supps" && !suppsSentOpen && !notesOpen && (
        <div className="mt-2.5 pt-2 border-t border-slate-200/60">
          {!chadNotesPresent && (
            <div className="text-[11px] text-amber-700 bg-amber-50 rounded px-2 py-1 mb-2 flex items-center gap-1">
              ⚠ Chad's supplement notes not yet entered — add them first
            </div>
          )}
          <button
            onClick={() => setSuppsSentOpen(true)}
            disabled={!chadNotesPresent}
            className="text-[12px] font-semibold px-3 py-1.5 rounded-lg bg-bardin-orange text-white hover:bg-bardin-orange/90 transition disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
          >
            📨 Supps Sent
          </button>
        </div>
      )}

      {stage === "ready_for_supps" && suppsSentOpen && !notesOpen && (
        <div className="mt-2.5 pt-2.5 border-t border-slate-200/60 space-y-2">
          <div className="text-[11px] font-semibold text-slate-700 uppercase tracking-wide">Date sent to Apex / insurance</div>
          <input
            type="date"
            value={sentDate}
            onChange={(e) => setSentDate(e.target.value)}
            className="text-[12px] border border-slate-200 rounded-lg px-3 py-2 bg-white w-full"
          />
          {autoText?.kind === "will_send" && sentDate && (
            suppsDateTextsAutomatically(sentDate, etDate(new Date())) ? (
              <div className="text-[11px] leading-snug text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5">
                💬 The homeowner gets the supplement text automatically 5–10 minutes after you confirm, with the email when
                there is one. Texts go out 8 AM–7 PM. No need to text them yourself.
              </div>
            ) : (
              <div className="text-[11px] leading-snug text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
                ⚠ That date is more than 3 days back, so no automatic text goes out. Text the homeowner from this card
                after you confirm.
              </div>
            )
          )}
          <div className="flex gap-2">
            <button
              onClick={handleSuppsSent}
              disabled={saving || !sentDate}
              className="flex-1 text-[12px] font-semibold px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition disabled:opacity-40"
            >
              {saving ? "Saving…" : "✓ Confirm & advance"}
            </button>
            <button
              onClick={() => setSuppsSentOpen(false)}
              className="text-[12px] px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Inline upload + advance for the two doc-upload stages — no nav to deal detail */}
      {!notesOpen && !suppsSentOpen && !uploadOpen && !residualOpen && (stage === "ready_for_updated_invoice" || stage === "supps_waiting_on_final_invoice" || stage === "supps_waiting_on_claims") && (
        <div className="mt-2.5 pt-2 border-t border-slate-200/60">
          <button
            onClick={() => {
              setUploadOpen(true);
              setUploadErr(null);
            }}
            className="text-[12px] font-semibold text-bardin-orange hover:text-bardin-orange/80 flex items-center gap-1 transition"
          >
            {stage === "ready_for_updated_invoice" ? "📄 Upload supplement invoice ▾" : "📋 Upload finalized insurance ▾"}
          </button>
        </div>
      )}

      {!notesOpen && !suppsSentOpen && uploadOpen && (stage === "ready_for_updated_invoice" || stage === "supps_waiting_on_final_invoice" || stage === "supps_waiting_on_claims") && (
        <div className="mt-2.5 pt-2.5 border-t border-slate-200/60 space-y-2.5">
          <div className="text-[11px] font-semibold text-slate-700 uppercase tracking-wide">
            {stage === "ready_for_updated_invoice"
              ? "Upload supplement invoice (Apex)"
              : "Upload finalized insurance paperwork"}
          </div>

          {/* File picker — styled drop zone, mirrors TransitionButton */}
          <label className="block border-2 border-dashed border-slate-300 rounded-lg p-2.5 text-center cursor-pointer hover:border-bardin-orange hover:bg-bardin-orange/5 transition">
            <div className="text-[11px] text-slate-600 font-medium">
              {uploadFile ? `📎 ${uploadFile.name}` : "Drop file or tap to choose"}
            </div>
            {uploadFile && (
              <div className="text-[10px] text-slate-400 mt-0.5">
                {(uploadFile.size / 1024).toFixed(0)} KB
              </div>
            )}
            <input
              type="file"
              accept="application/pdf,image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0] ?? null;
                setUploadFile(f);
                setUploadErr(null);
              }}
            />
          </label>

          {stage === "ready_for_updated_invoice" ? (
            <div>
              <div className="text-[11px] font-medium text-slate-600 mb-0.5">Supplement amount requested</div>
              <div className="text-[10px] leading-snug text-slate-400 mb-1">
                The ADDED amount requested from the carrier — not Apex's revised estimate total. Never shown to the homeowner until approved.
              </div>
              <div className="relative">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-[12px]">$</span>
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  placeholder="0.00"
                  value={uploadInvAmt}
                  onChange={(e) => setUploadInvAmt(e.target.value)}
                  className="w-full pl-6 pr-2.5 py-2 text-[12px] border border-slate-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-bardin-orange/40"
                />
              </div>
              <SupplementEntryWarning
                entered={Number(uploadInvAmt) || 0}
                rcv={Number(r.rcv_total ?? 0) || 0}
                onUse={(v) => setUploadInvAmt(String(v))}
              />
              <div className="mt-2 text-[11px] font-medium text-slate-600 mb-1">Date Apex's estimate arrived</div>
              <input
                type="date"
                value={uploadEstDate}
                onChange={(e) => setUploadEstDate(e.target.value)}
                className="w-full px-2.5 py-2 text-[12px] border border-slate-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-bardin-orange/40"
              />
            </div>
          ) : (
            <>
              <div>
                <div className="text-[11px] font-medium text-slate-600 mb-0.5">Approved supplement amount</div>
                <div className="text-[10px] leading-snug text-slate-400 mb-1">The added amount the carrier approved — this lands on the homeowner's invoice.</div>
                <div className="relative">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-[12px]">$</span>
                  <input
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    placeholder="0.00"
                    value={uploadAppAmt}
                    onChange={(e) => setUploadAppAmt(e.target.value)}
                    className="w-full pl-6 pr-2.5 py-2 text-[12px] border border-slate-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-bardin-orange/40"
                  />
                </div>
              </div>
              <div>
                <div className="text-[11px] font-medium text-slate-600 mb-1">Insurance approval date</div>
                <input
                  type="date"
                  value={uploadAppDate}
                  onChange={(e) => setUploadAppDate(e.target.value)}
                  className="w-full px-2.5 py-2 text-[12px] border border-slate-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-bardin-orange/40"
                />
              </div>
            </>
          )}

          {uploadErr && (
            <div className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
              {uploadErr}
            </div>
          )}

          <div className="flex gap-2">
            <button
              onClick={handleUploadAndAdvance}
              disabled={uploadSaving}
              className="flex-1 text-[12px] font-semibold px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition disabled:opacity-40"
            >
              {uploadSaving
                ? "Uploading…"
                : stage === "ready_for_updated_invoice"
                ? "✓ Upload & advance to waiting on final invoice"
                : "✓ Upload & advance to collecting"}
            </button>
            <button
              onClick={() => {
                setUploadOpen(false);
                setUploadFile(null);
                setUploadErr(null);
              }}
              disabled={uploadSaving}
              className="text-[12px] px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition disabled:opacity-40"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── Targeted residual write-off (pre-collecting supps stages). Jul 1, 2026. ──
          Clears a small first-payment/commission-gate shortfall so a blocked
          commission can be approved, WITHOUT forgiving the full uncollected
          final-invoice balance or advancing the stage. */}
      {!notesOpen && !suppsSentOpen && !uploadOpen && !miscOpen && !reopenOpen && !residualOpen && SUPP_RESIDUAL_STAGES.has(stage) && (
        <div className="mt-2.5 pt-2 border-t border-slate-200/60 flex flex-wrap items-center gap-2">
          <button
            onClick={() => {
              setResidualOpen(true);
              setResidualErr(null);
              setResidualAmount("");
              setResidualReason("");
            }}
            title="Forgive a small uncollectable residual (e.g. a short ACV check) so commissions can be approved. Does not change the stage."
            className="text-[12px] font-semibold px-3 py-1.5 rounded-lg border border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-100 transition flex items-center gap-1.5"
          >
            💸 Write off residual…
          </button>
        </div>
      )}

      {/* ── Residual write-off confirm panel (supps stages) ── */}
      {!notesOpen && !suppsSentOpen && !uploadOpen && residualOpen && SUPP_RESIDUAL_STAGES.has(stage) && (
        <div className="mt-2.5 pt-2.5 border-t border-slate-200/60 space-y-2.5">
          <div className="text-[11px] font-semibold text-slate-700 uppercase tracking-wide">
            Write off residual
          </div>
          <div className="text-[11px] text-slate-500">
            Forgives a specific uncollectable amount (e.g. an ACV check that came in a few dollars short) so a
            blocked commission can be approved. Reduces AR by that amount and is recorded in the finance audit log.
            It does <strong>not</strong> change the deal's stage or forgive the rest of the balance.
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[12px] text-slate-600">Amount to write off</span>
            <div className="relative">
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[12px] text-slate-500">$</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={residualAmount}
                onChange={(e) => setResidualAmount(e.target.value)}
                placeholder="0.00"
                className="w-28 text-[12px] border border-slate-300 rounded pl-5 pr-2 py-1.5"
              />
            </div>
          </div>
          <input
            type="text"
            value={residualReason}
            onChange={(e) => setResidualReason(e.target.value)}
            placeholder="Reason (optional) — e.g. ACV check short / non-collect residual"
            className="w-full text-[12px] border border-slate-300 rounded px-2.5 py-1.5"
          />
          {residualErr && (
            <div className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
              {residualErr}
            </div>
          )}
          <div className="flex gap-2">
            <button
              onClick={handleWriteOffResidual}
              disabled={residualSaving || !(Number(residualAmount) > 0)}
              className="flex-1 text-[12px] font-semibold px-3 py-1.5 rounded-lg bg-amber-600 text-white hover:bg-amber-700 transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {residualSaving
                ? "Writing off…"
                : `💸 Write off${Number(residualAmount) > 0 ? ` $${Number(residualAmount).toFixed(2)}` : ""}`}
            </button>
            <button
              onClick={() => {
                setResidualOpen(false);
                setResidualErr(null);
              }}
              disabled={residualSaving}
              className="text-[12px] px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition disabled:opacity-40"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── "All money collected" inline advance (collecting/delinquent) ── */}
      {!notesOpen && !suppsSentOpen && !uploadOpen && !collectedOpen && !writeoffOpen && !markDelinqOpen && (stage === "collecting" || stage === "delinquent") && (
        <div className="mt-2.5 pt-2 border-t border-slate-200/60 flex flex-wrap items-center gap-2">
          <button
            onClick={() => {
              setCollectedOpen(true);
              setCollectedErr(null);
            }}
            disabled={!balanceZeroMet}
            title={
              balanceZeroMet
                ? "Advance to register warranty"
                : balanceN > 0
                ? `Still owed: $${balanceN.toFixed(2)}`
                : totalReceivedN <= 0
                ? "No payments received yet"
                : ""
            }
            className={
              "text-[12px] font-semibold px-3 py-1.5 rounded-lg transition flex items-center gap-1.5 " +
              (balanceZeroMet
                ? "bg-emerald-600 text-white hover:bg-emerald-700"
                : "bg-slate-100 text-slate-400 cursor-not-allowed")
            }
          >
            💰 All money collected → register warranty
            {!balanceZeroMet && balanceN > 0 && (
              <span className="text-[10px] font-normal opacity-80">
                · ${balanceN.toFixed(2)} still owed
              </span>
            )}
          </button>
          {/* Forgive a small uncollectable residual (no approval). May 21, 2026. */}
          {balanceN > 0 && (
            <button
              onClick={() => {
                setWriteoffOpen(true);
                setWriteoffErr(null);
                setWriteoffReason("");
              }}
              title={`Forgive the $${balanceN.toFixed(2)} still owed and advance`}
              className="text-[12px] font-semibold px-3 py-1.5 rounded-lg border border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-100 transition flex items-center gap-1.5"
            >
              💸 Write off remaining ${balanceN.toFixed(2)}
            </button>
          )}
          {/* Manually flag delinquent without waiting for the 30-day cron. May 26, 2026. */}
          {stage === "collecting" && (
            <button
              onClick={() => {
                setMarkDelinqOpen(true);
                setMarkDelinqErr(null);
                setMarkDelinqReason("");
              }}
              title="Move this deal to delinquent now (skips the 30-day auto-promote)"
              className="text-[12px] font-semibold px-3 py-1.5 rounded-lg border border-red-300 text-red-700 bg-red-50 hover:bg-red-100 transition flex items-center gap-1.5"
            >
              ⚠ Mark delinquent
            </button>
          )}
        </div>
      )}

      {/* ── "Mark delinquent" confirm panel (collecting → delinquent) ── */}
      {!notesOpen && !suppsSentOpen && !uploadOpen && markDelinqOpen && stage === "collecting" && (
        <div className="mt-2.5 pt-2.5 border-t border-slate-200/60 space-y-2.5">
          <div className="text-[11px] font-semibold text-slate-700 uppercase tracking-wide">
            Mark delinquent
          </div>
          <div className="text-[11px] text-slate-600 space-y-0.5 bg-slate-50 rounded-lg px-2.5 py-2">
            <div className="flex justify-between">
              <span>Balance outstanding</span>
              <strong className="text-red-700">${balance.toFixed(2)}</strong>
            </div>
            <div className="flex justify-between">
              <span>Days in collecting</span>
              <strong className="text-slate-700">{days}</strong>
            </div>
          </div>
          <div className="text-[11px] text-slate-500">
            Moves the deal from <strong>collecting</strong> to <strong>delinquent</strong> now,
            skipping the 30-day auto-promote. Recorded in the finance audit log and Shannon is notified.
          </div>
          <input
            type="text"
            value={markDelinqReason}
            onChange={(e) => setMarkDelinqReason(e.target.value)}
            placeholder="Reason (optional) — e.g. HO stopped responding"
            className="w-full text-[12px] border border-slate-300 rounded px-2.5 py-1.5"
          />
          {markDelinqErr && (
            <div className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
              {markDelinqErr}
            </div>
          )}
          <div className="flex gap-2">
            <button
              onClick={handleMarkDelinquent}
              disabled={markDelinqSaving}
              className="flex-1 text-[12px] font-semibold px-3 py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700 transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {markDelinqSaving ? "Marking…" : "⚠ Confirm — mark delinquent"}
            </button>
            <button
              onClick={() => {
                setMarkDelinqOpen(false);
                setMarkDelinqErr(null);
              }}
              disabled={markDelinqSaving}
              className="text-[12px] px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition disabled:opacity-40"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── "Write off remaining balance" confirm panel ── */}
      {!notesOpen && !suppsSentOpen && !uploadOpen && writeoffOpen && (stage === "collecting" || stage === "delinquent") && (
        <div className="mt-2.5 pt-2.5 border-t border-slate-200/60 space-y-2.5">
          <div className="text-[11px] font-semibold text-slate-700 uppercase tracking-wide">
            Write off remaining balance
          </div>
          <div className="text-[11px] text-slate-600 space-y-0.5 bg-slate-50 rounded-lg px-2.5 py-2">
            <div className="flex justify-between">
              <span>Balance to write off</span>
              <strong className="text-amber-700">${balanceN.toFixed(2)}</strong>
            </div>
            <div className="flex justify-between">
              <span>Total received</span>
              <strong className="text-slate-700">${totalReceivedN.toFixed(2)}</strong>
            </div>
          </div>
          <div className="text-[11px] text-slate-500">
            Forgives the remaining ${balanceN.toFixed(2)} (Bardin won't collect it) and advances the deal to
            {" "}<strong>register warranty</strong>. Recorded in the finance audit log. Cash-received totals are unchanged.
          </div>
          <input
            type="text"
            value={writeoffReason}
            onChange={(e) => setWriteoffReason(e.target.value)}
            placeholder="Reason (optional) — e.g. ACV check came in short"
            className="w-full text-[12px] border border-slate-300 rounded px-2.5 py-1.5"
          />
          {writeoffErr && (
            <div className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
              {writeoffErr}
            </div>
          )}
          <div className="flex gap-2">
            <button
              onClick={handleWriteOffRemaining}
              disabled={writeoffSaving || balanceN <= 0}
              className="flex-1 text-[12px] font-semibold px-3 py-1.5 rounded-lg bg-amber-600 text-white hover:bg-amber-700 transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {writeoffSaving ? "Writing off…" : `💸 Write off $${balanceN.toFixed(2)} & advance`}
            </button>
            <button
              onClick={() => {
                setWriteoffOpen(false);
                setWriteoffErr(null);
              }}
              disabled={writeoffSaving}
              className="text-[12px] px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition disabled:opacity-40"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {!notesOpen && !suppsSentOpen && !uploadOpen && collectedOpen && (stage === "collecting" || stage === "delinquent") && (
        <div className="mt-2.5 pt-2.5 border-t border-slate-200/60 space-y-2.5">
          <div className="text-[11px] font-semibold text-slate-700 uppercase tracking-wide">
            Confirm all money collected
          </div>
          <div className="text-[11px] text-slate-600 space-y-0.5 bg-slate-50 rounded-lg px-2.5 py-2">
            <div className="flex justify-between">
              <span>Balance remaining</span>
              <strong className={balanceN <= 0 ? "text-emerald-700" : "text-red-700"}>
                ${balanceN.toFixed(2)}
              </strong>
            </div>
            <div className="flex justify-between">
              <span>Total received</span>
              <strong className="text-slate-700">${totalReceivedN.toFixed(2)}</strong>
            </div>
          </div>
          {!balanceZeroMet && (
            <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
              ⚠ {balanceN > 0
                ? `Still owed $${balanceN.toFixed(2)} — record the remaining payment first.`
                : "No payments received yet — record a payment first."}
            </div>
          )}
          {collectedErr && (
            <div className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
              {collectedErr}
            </div>
          )}
          <div className="flex gap-2">
            <button
              onClick={handleAllMoneyCollected}
              disabled={collectedSaving || !balanceZeroMet}
              className="flex-1 text-[12px] font-semibold px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {collectedSaving ? "Advancing…" : "✓ Confirm & advance to register warranty"}
            </button>
            <button
              onClick={() => {
                setCollectedOpen(false);
                setCollectedErr(null);
              }}
              disabled={collectedSaving}
              className="text-[12px] px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition disabled:opacity-40"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── "Mark Closed Won" inline advance (register_oc_warranty) ── */}
      {!notesOpen && !miscOpen && !closedWonOpen && stage === "register_oc_warranty" && (
        <div className="mt-2.5 pt-2 border-t border-slate-200/60">
          {!closedWonReady && (
            <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-2">
              ⚠ Waiting on the OC warranty certificate — upload it on the deal page first.
            </div>
          )}
          {closedWonReady && (
            <div className="text-[11px] text-emerald-700 mb-2">
              ✓ OC warranty certificate on file
            </div>
          )}
          <button
            onClick={() => {
              setClosedWonOpen(true);
              setClosedWonErr(null);
            }}
            disabled={!closedWonReady}
            title={
              closedWonReady
                ? "Close this deal as won"
                : "Register the OC warranty + upload the certificate first"
            }
            className={
              "text-[12px] font-semibold px-3 py-1.5 rounded-lg transition flex items-center gap-1.5 " +
              (closedWonReady
                ? "bg-emerald-600 text-white hover:bg-emerald-700"
                : "bg-slate-100 text-slate-400 cursor-not-allowed")
            }
          >
            🏆 Mark Closed Won
          </button>
        </div>
      )}

      {!notesOpen && !miscOpen && closedWonOpen && stage === "register_oc_warranty" && (
        <div className="mt-2.5 pt-2.5 border-t border-slate-200/60 space-y-2.5">
          <div className="text-[11px] font-semibold text-slate-700 uppercase tracking-wide">
            Confirm Closed Won
          </div>
          <div className="text-[11px] text-slate-600 bg-slate-50 rounded-lg px-2.5 py-2 space-y-0.5">
            <div className="flex justify-between">
              <span>OC certificate</span>
              <strong className={ocCertUploaded ? "text-emerald-700" : "text-red-700"}>
                {ocCertUploaded ? "On file" : "Missing"}
              </strong>
            </div>
          </div>
          <div className="text-[11px] text-slate-500">
            This advances the deal to <strong>Closed Won</strong> — the final lifecycle stage.
          </div>
          {closedWonErr && (
            <div className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
              {closedWonErr}
            </div>
          )}
          <div className="flex gap-2">
            <button
              onClick={handleMarkClosedWon}
              disabled={closedWonSaving || !closedWonReady}
              className="flex-1 text-[12px] font-semibold px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {closedWonSaving ? "Closing…" : "🏆 Confirm Closed Won"}
            </button>
            <button
              onClick={() => {
                setClosedWonOpen(false);
                setClosedWonErr(null);
              }}
              disabled={closedWonSaving}
              className="text-[12px] px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition disabled:opacity-40"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── Complete (Closed Won): Reopen to Collecting ── */}
      {!notesOpen && !miscOpen && !reopenOpen && stage === "closed_won" && (
        <div className="mt-2.5 pt-2 border-t border-slate-200/60">
          <div className="text-[11px] text-slate-500 mb-2">
            ✓ Completed job. Reopen to re-invoice a balance — e.g. a payment that didn't clear.
          </div>
          <button
            onClick={() => {
              setReopenOpen(true);
              setReopenErr(null);
            }}
            title="Send this completed job back to Collecting so you can re-invoice"
            className="text-[12px] font-semibold px-3 py-1.5 rounded-lg border border-bardin-orange/40 text-bardin-orange bg-bardin-orange/5 hover:bg-bardin-orange/10 transition flex items-center gap-1.5"
          >
            ↩ Reopen to Collecting
          </button>
        </div>
      )}

      {!notesOpen && !miscOpen && reopenOpen && stage === "closed_won" && (
        <div className="mt-2.5 pt-2.5 border-t border-slate-200/60 space-y-2.5">
          <div className="text-[11px] font-semibold text-slate-700 uppercase tracking-wide">
            Reopen to Collecting
          </div>
          <div className="text-[11px] text-slate-600 bg-slate-50 rounded-lg px-2.5 py-2 space-y-0.5">
            <div className="flex justify-between">
              <span>Invoice total</span>
              <strong className="text-slate-700">${(Number(r.final_invoice_amount) || 0).toFixed(2)}</strong>
            </div>
            <div className="flex justify-between">
              <span>Received</span>
              <strong className="text-slate-700">${totalReceivedN.toFixed(2)}</strong>
            </div>
          </div>
          <div className="text-[11px] text-slate-500">
            Moves this completed job back to <strong>Collecting</strong> and refreshes the balance from
            recorded payments, so you can send a new invoice. Recorded in the activity log.
          </div>
          {reopenErr && (
            <div className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
              {reopenErr}
            </div>
          )}
          <div className="flex gap-2">
            <button
              onClick={handleReopenToCollecting}
              disabled={reopenSaving}
              className="flex-1 text-[12px] font-semibold px-3 py-1.5 rounded-lg bg-bardin-orange text-white hover:bg-bardin-orange/90 transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {reopenSaving ? "Reopening…" : "↩ Confirm — reopen to Collecting"}
            </button>
            <button
              onClick={() => {
                setReopenOpen(false);
                setReopenErr(null);
              }}
              disabled={reopenSaving}
              className="text-[12px] px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition disabled:opacity-40"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Send Invoice (Variant C) — appears once supps fields are filled. */}
      <SendInvoiceMount row={r} stage={stage} onRefresh={onRefresh} />
    </li>
  );
}

// Wrapper that surfaces the SendInvoicePanel on supps-lifecycle cards only.
// Hides itself outside the relevant window so it doesn't add noise to the
// "ready_for_supps" pre-entry stage where there's nothing to invoice yet.
function SendInvoiceMount({
  row: r,
  stage,
  onRefresh,
}: {
  row: any;
  stage: string;
  onRefresh?: () => void;
}) {
  const { member, session } = useAuth();
  // Only show on stages where invoicing actually makes sense.
  const showStages = new Set([
    "ready_for_updated_invoice",
    "supps_waiting_on_final_invoice",
    "supps_waiting_on_claims",
    "collecting",
    "delinquent",
    "register_oc_warranty",
  ]);
  if (!showStages.has(stage)) return null;
  // currentUserId must be the Supabase auth id (auth.uid()) — the
  // invoice_template_overrides RLS policy keys on it. member.id is the
  // team_roster id, a different value, so passing it made every template
  // save fail the row-level security check.
  if (!member?.id || !session?.user?.id) return null;
  return (
    <div className="mt-3 pt-3 border-t border-slate-200/70">
      <SendInvoicePanel
        supabase={supabase}
        deal={r}
        currentUserId={session.user.id}
        currentUserFullName={member.full_name ?? undefined}
        onUpdated={onRefresh}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// KPI strip — view-specific cards across the top of the board.
// ---------------------------------------------------------------------------

function FinanceKPIs({
  view,
  onTab: _onTab,
}: {
  view: "shannon" | "chad" | "executive";
  onTab: (k: FinanceTabKey) => void;
}) {
  // For Ship 4 v1: dynamic counts from a few cheap COUNT(*) queries. Static
  // numbers can be replaced with live aggregates later via a server view.
  const [counts, setCounts] = useState<{ openSupps: number; collecting: number; delinquent: number; pendingComm: number; pendingDraws: number; openAR: number }>(
    { openSupps: 0, collecting: 0, delinquent: 0, pendingComm: 0, pendingDraws: 0, openAR: 0 },
  );

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const [openSupps, collecting, delinquent, pendingComm, pendingDraws, openAR] = await Promise.all([
        supabase.from("deals").select("*", { count: "exact", head: true })
          .in("stage", ["ready_for_supps", "ready_for_updated_invoice", "supps_waiting_on_final_invoice", "supps_waiting_on_claims"]),
        supabase.from("deals").select("*", { count: "exact", head: true }).eq("stage", "collecting"),
        supabase.from("deals").select("*", { count: "exact", head: true }).eq("stage", "delinquent"),
        // Count all deals awaiting commission approval (both old-flow and supplement-lifecycle).
        supabase.from("deals").select("*", { count: "exact", head: true })
          .not("commission_ready_date", "is", null).eq("commission_approved", false).eq("commission_paid", false),
        supabase.from("commission_draws").select("*", { count: "exact", head: true }).eq("approval_status", "pending"),
        supabase.from("deals").select("*", { count: "exact", head: true })
          .in("stage", ["collecting", "delinquent", "register_oc_warranty"]),
      ]);
      if (cancelled) return;
      setCounts({
        openSupps: openSupps.count ?? 0,
        collecting: collecting.count ?? 0,
        delinquent: delinquent.count ?? 0,
        pendingComm: pendingComm.count ?? 0,
        pendingDraws: pendingDraws.count ?? 0,
        openAR: openAR.count ?? 0,
      });
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const cards = view === "shannon"
    ? [
        { lbl: "Open AR",          val: fmtInt(counts.openAR),       sub: `${counts.delinquent} delinquent`, cls: counts.delinquent > 0 ? "text-red-600" : undefined, tab: "ar" as FinanceTabKey },
        { lbl: "Open supp deals",  val: fmtInt(counts.openSupps),    sub: "ready / in flight", cls: "text-amber-600", tab: "supplements" as FinanceTabKey },
        { lbl: "Collecting",       val: fmtInt(counts.collecting),   sub: "awaiting HO payment", cls: "text-emerald-600", tab: "supplements" as FinanceTabKey },
        { lbl: "Delinquent",       val: fmtInt(counts.delinquent),   sub: "30+ days unpaid", cls: counts.delinquent > 0 ? "text-red-600" : "text-slate-500", tab: "supplements" as FinanceTabKey },
        { lbl: "Pending comm",     val: fmtInt(counts.pendingComm),  sub: "awaiting approval", cls: "text-blue-600", tab: "commissions" as FinanceTabKey },
      ]
    : view === "chad"
    ? [
        { lbl: "Approval queue",   val: fmtInt(counts.pendingComm),  sub: "RM/TL/ASM commissions", cls: "text-bardin-orange", tab: "commissions" as FinanceTabKey },
        { lbl: "Supps awaiting notes", val: fmtInt(counts.openSupps), sub: "needs your visit", cls: "text-amber-600", tab: "supplements" as FinanceTabKey },
        { lbl: "Draws pending",    val: fmtInt(counts.pendingDraws), sub: "your decision", cls: "text-purple-600", tab: "draws" as FinanceTabKey },
        { lbl: "Today's blocked",  val: "—",                          sub: "pre-build / crew gates", cls: "text-slate-500", tab: "commissions" as FinanceTabKey },
        { lbl: "This cycle",       val: "—",                          sub: "crew + comm to pay", cls: "text-slate-500", tab: "commissions" as FinanceTabKey },
      ]
    : [
        { lbl: "Open AR",          val: fmtInt(counts.openAR),       sub: `${counts.delinquent} delinquent`, cls: counts.delinquent > 0 ? "text-red-600" : undefined, tab: "ar" as FinanceTabKey },
        { lbl: "Pending comm",     val: fmtInt(counts.pendingComm),  sub: "approval queue", cls: "text-blue-600", tab: "commissions" as FinanceTabKey },
        { lbl: "Open supp deals",  val: fmtInt(counts.openSupps),    sub: "in flight", cls: "text-amber-600", tab: "supplements" as FinanceTabKey },
        { lbl: "Collecting + delinquent", val: fmtInt(counts.collecting + counts.delinquent), sub: "awaiting payment", cls: "text-emerald-600", tab: "supplements" as FinanceTabKey },
        { lbl: "Draws outstanding", val: fmtInt(counts.pendingDraws), sub: "pending Chad", cls: "text-purple-600", tab: "draws" as FinanceTabKey },
      ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
      {cards.map((k) => (
        <button
          key={k.lbl}
          onClick={() => _onTab(k.tab)}
          className="text-left bg-white border border-slate-200 rounded-xl p-3 hover:shadow-md hover:-translate-y-0.5 transition-all"
        >
          <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">{k.lbl}</div>
          <div className={"text-2xl font-extrabold mt-1 " + (k.cls ?? "text-bardin-ink")}>{k.val}</div>
          {k.sub && <div className="text-[10px] text-slate-500 mt-0.5">{k.sub}</div>}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared primitives
// ---------------------------------------------------------------------------

function Panel({ children, error }: { children: React.ReactNode; error?: boolean }) {
  return (
    <div className={error ? "bg-red-50 text-red-700 p-4 rounded" : "bg-white rounded-xl shadow p-5"}>
      {children}
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div className="text-sm text-slate-500 py-2">{children}</div>;
}

function Kpi({
  label,
  value,
  sub,
  valueClass,
}: {
  label: string;
  value: string;
  sub?: string;
  valueClass?: string;
}) {
  return (
    <div className="bg-white rounded-xl shadow p-4">
      <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</div>
      <div className={"text-2xl font-semibold mt-1 " + (valueClass ?? "text-bardin-ink")}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}
